'use client';

import { memo, useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, MessageCircle, Bookmark, Share2, MoreHorizontal, Send,
  Repeat2, Trash2, Copy, Flag, Eye, Globe, Users, Lock,
  X, Youtube, Music2,
  Download, FileText, FileCode, FileArchive, FileSpreadsheet,
  CornerDownRight, Loader2, RotateCcw,
  // Phase 3: FB menu
  Pin, Bell, Archive, EyeOff, Clock,
} from 'lucide-react';
import { useSocialStore } from '@/store/socialStore';
import { socialApi, messagingApi, type MessagingThread, api } from '@/lib/api';
import MentionAutocomplete from '@/components/social/MentionAutocomplete';
import { RenderContentWithCode } from '@/components/social/CodeBlock';
import EmojiPickerPopover from '@/components/messaging/EmojiPickerPopover';
import GifPicker from '@/components/messaging/GifPicker';
import StickerPicker from '@/components/messaging/StickerPicker';
import PostPoll from '@/components/social/PostPoll';
import SocialSavePopover, {
  type SaveCollection,
} from '@/components/social/SocialSavePopover';
import SocialSavePopoverV2 from '@/components/social/SocialSavePopoverV2';
import { type MusicTrackMini } from '@/components/social/MusicSticker';
import PostMusicPlayer from '@/components/social/PostMusicPlayer';
import { useAuthStore } from '@/store/authStore';
import { useMessagingStore } from '@/store/messagingStore';
import { getMediaUrl } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { linkifyToNodes } from '@/lib/linkify';
import { playPop } from '@/lib/uiSound';
import SmartImage from '@/components/ui/SmartImage';
import { getFeedSound, setFeedSound, subscribeFeedSound } from '@/lib/feedVideoSound';
import type { SocialPost, SocialComment, SocialMedia, ReactionType, ReactionBreakdown, FeedCollection, FeedPostSaveContext, FeedSaveResult } from '@/types/social';
import { REACTION_META, REACTION_PICKER_ORDER, WOW_META, EMPTY_REACTION_BREAKDOWN } from '@/types/social';
import { socialKeys, type SocialFeedResponse } from '@/hooks/useSocialQueries';
import { categoryLabel, categoryAccent } from '@/lib/botRegistry';
import { formatRelative } from '@/lib/formatDate';
import { toast } from 'sonner';
import ProAvatarRing, { ProTag } from '@/components/common/ProAvatarRing';

const VISIBILITY_META: Record<string, { icon: any; label: string; color: string }> = {
  PUBLIC: { icon: Globe, label: 'Công khai', color: '#94a3b8' },
  FRIENDS: { icon: Users, label: 'Bạn bè', color: '#22c55e' },
  PRIVATE: { icon: Lock, label: 'Chỉ mình tôi', color: '#f59e0b' },
};

// ─── Multi-emoji reactions (added 2026-06-20) ─────────────────────
// Drives the reaction picker popover. `key` is the ReactionType
// the server now understands (LIKE / LOVE / HAHA / SAD / ANGRY).
// `meta` is the visual mapping (emoji + label + colour) sourced
// from types/social.ts so the new code and any future consumer
// share one source of truth.
const REACTION_KEYS: ReactionType[] = ['LIKE', 'LOVE', 'HAHA', 'SAD', 'ANGRY'];

const MAX_PREVIEW_LENGTH = 600;

/**
 * Cắt phần xem trước của một bài dài.
 *
 * Cắt thẳng ở ký tự thứ 600 có thể rơi vào GIỮA một khối ```code```, và
 * khi đó ba dấu huyền mở không còn dấu đóng: bộ tách khối code không nhận
 * ra nó nữa nên người đọc thấy nguyên chuỗi "```java" nằm giữa bài. Ở đây
 * ta kéo dài tới dấu đóng nếu nó ở gần (khối code ngắn — xem trọn vẹn còn
 * hay hơn), còn không thì lùi về trước dấu mở.
 */
function previewOf(text: string): string {
  let end = MAX_PREVIEW_LENGTH;
  const openFences = (text.slice(0, end).match(/```/g) || []).length;
  if (openFences % 2 === 1) {
    const close = text.indexOf('```', end);
    if (close >= 0 && close - end <= 400) {
      end = close + 3;
    } else {
      const open = text.lastIndexOf('```', end);
      if (open > 0) end = open;
    }
  }
  return text.slice(0, end).trimEnd() + '…';
}

// Matches the `activeColor` previously passed to ActionButton for
// the save state. Centralised here so the new bookmark button and
// the popover use exactly the same amber.
const NEON_AMBER = '#f59e0b';

// "#rrggbb" + alpha → "rgba(r,g,b,a)" — used to tint the news-category chip
// from a bot's accent colour. Falls back to brand purple on bad input.
function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return `rgba(79,91,213,${alpha})`;
  const int = parseInt(m[1], 16);
  return `rgba(${(int >> 16) & 255},${(int >> 8) & 255},${int & 255},${alpha})`;
}

interface PostCardProps {
  post: SocialPost;
  onToggleLike?: (postId: number) => Promise<void>;
  onToggleSave?: (postId: number) => Promise<void>;
  onDelete?: (postId: number) => Promise<void>;
  /**
   * Fired when the user clicks the "Theater" button on a video
   * media item. The page-level wrapper opens the fullscreen
   * TheaterMode modal. PostCard does not own the modal — it
   * only emits the intent.
   */
  onOpenTheater?: (postId: number) => void;
  /**
   * Detail mode (used by <PostCommentModal />). When true the card
   * renders with the comment LIST always expanded and does NOT
   * render its own inline comment composer — the modal supplies a
   * pinned composer. The "Bình luận" button becomes a no-op so it
   * can't recursively re-open the modal.
   */
  detailMode?: boolean;
}

// ─── Memoisation ───────────────────────────────────────────────
// PostCard is heavy (multiple sub-views, video preview, comment
// thread, action row, save popover). Without memo, every socket
// tick that touches the social store re-renders every card in the
// feed — which is exactly the jank users see when they're scrolled
// mid-list. The custom equality fn compares only what the user can
// actually see change on the card surface; deeper state lives
// inside the card and is owned by it (showComments, etc.).
function PostCardImpl({ post, onToggleLike, onToggleSave, onDelete, onOpenTheater, detailMode = false }: PostCardProps) {
  const [showComments, setShowComments] = useState(false);
  // In detail mode (inside the comment modal) the comment list is
  // always open and cannot be collapsed.
  const commentsOpen = detailMode || showComments;
  const [commentText, setCommentText] = useState('');
  // User ids the user @'d in the current comment. Populated by
  // MentionAutocomplete's onPick callback. We send this in the
  // createComment payload so the backend can fan out NEW_MENTION
  // notifications to each mentioned user. The set is cleared on
  // submit and on text change so the next comment starts fresh.
  const [commentMentions, setCommentMentions] = useState<Set<number>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Rich media (GIF / sticker) pending on the comment composer, and
  // which media picker popover is open. Mirrors the messenger composer.
  const [commentMedia, setCommentMedia] = useState<{ url: string; kind: 'gif' | 'sticker' } | null>(null);
  const [commentPicker, setCommentPicker] = useState<'emoji' | 'gif' | 'sticker' | null>(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  // Phase 6 — "Gửi qua Messenger" picker. True when the user
  // taps the Send icon on the action bar and the thread picker
  // modal is open.
  const [showMessengerPicker, setShowMessengerPicker] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  // ─── Saved Collections popover (legacy single-folder, kept
  //      for callers that still wire it up). ───────────────────
  const [showSavePopover, setShowSavePopover] = useState(false);
  const saveButtonRef = useRef<HTMLButtonElement | null>(null);
  // Phase 5 home upgrade: ref for the comment composer input so the
  // MentionAutocomplete component can listen to its caret position.
  const commentInputRef = useRef<HTMLInputElement | null>(null);
  // Anchor for the comment media pickers so they portal above this row
  // (escaping the post card's overflow-hidden that was clipping them).
  const commentBarRef = useRef<HTMLDivElement | null>(null);
  // Cache of the user's existing collections. We hydrate this on
  // first popover open. Lazily loaded so the PostCard stays cheap
  // when no one opens the popover.
  const [cachedCollections, setCachedCollections] = useState<SaveCollection[]>([]);
  const [collectionsLoaded, setCollectionsLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const longPressTimer = useRef<any>(null);

  // ─── Saved Collections V2 popover (added 2026-06-20) ────────
  // Multi-folder variant powered by FeedCollection +
  // FeedSavedPost. State is separate from the legacy
  // single-folder popover above so we can A/B them or roll
  // back without touching unrelated code. The V2 popover
  // is the default for new saves; the legacy one stays as
  // a compatibility fallback for the existing single-folder
  // route.
  const [showSavePopoverV2, setShowSavePopoverV2] = useState(false);
  // Cached FeedCollection rows for the popover. Fetched once
  // per popover open. The parent (e.g. the /saved page) can
  // also pre-load this via `initialCollections`.
  const [collectionsV2, setCollectionsV2] = useState<FeedCollection[]>(
    () => ((post as any).__initialCollections as FeedCollection[] | undefined) ?? [],
  );
  // Pre-tick state: which collections this post is currently
  // saved into. Cached so reopening the popover is instant.
  const [saveContext, setSaveContext] = useState<FeedPostSaveContext | null>(
    () => (post as any).__saveContext as FeedPostSaveContext | null ?? null,
  );
  const [collectionsV2Loaded, setCollectionsV2Loaded] = useState(
    Boolean((post as any).__initialCollections) || Boolean((post as any).__saveContext),
  );
  const [collectionsV2Loading, setCollectionsV2Loading] = useState(false);
  // Whether the next click on the bookmark button should open
  // the V2 popover. We always open V2 for new code; the legacy
  // popover is left alone.
  const { toggleLike, toggleReaction, toggleSave, loadComments, commentsByPost, loadMoreComments, commentsHasMoreByPost, isLoadingComments, addOptimisticComment, deletePost } = useSocialStore();
  const { user: currentUser } = useAuthStore();
  // Phase 6 — "Send to Messenger" picker. We don't load the threads
  // on mount because most users won't tap Share on every card;
  // instead the modal triggers loadThreads() lazily when it opens
  // so we don't hammer the API on feed scroll.
  const loadThreads = useMessagingStore((s) => s.loadThreads);
  const qc = useQueryClient();

  // ─── Query cache helpers (added 2026-06-20) ────────────────────
  // The feed page renders from the TanStack Query cache
  // (`useSocialFeed`), NOT from Zustand. Zustand mutations alone
  // never trigger a re-render of the cached data — we must
  // write to the cache directly too.
  //
  // TanStack Query v5 `setQueriesData({ queryKey: [...] })` uses
  // `matchQuery` with `exact=true` (default) for key comparison.
  // `partialMatchKey` checks object-keys but NOT array prefixes.
  // Result: `setQueriesData({ queryKey: ['social','feed'] })` does
  // NOT match a cache entry stored under
  // `['social','feed',{limit:20}]` — causing a silent no-op.
  //
  // Fix: use `qc.getQueryCache().findAll({ predicate })` to
  // iterate all cached queries and manually match on the feed
  // prefix. We also call `qc.invalidateQueries` afterward so the
  // next background refetch syncs with the server.

  /** Return all Query objects in the cache whose key starts with
   *  `['social','feed']`. This is the reliable way to match feed
   *  pages regardless of their pagination params. */
  const getFeedQueries = () =>
    qc.getQueryCache().getAll().filter(
      (q) => Array.isArray(q.queryKey) &&
             q.queryKey[0] === 'social' &&
             q.queryKey[1] === 'feed',
    );

  /** Snapshot the feed cache entries before a mutation. */
  const snapshotFeed = () =>
    getFeedQueries().map((q) => [q.queryKey as string[], q.state.data as SocialFeedResponse | undefined]);

  /** Patch a single post in all cached feed queries. Calls
   *  `invalidateFeed` afterward so the next refetch reconciles. */
  const patchFeed = (postId: number, updater: (p: SocialPost) => SocialPost) => {
    for (const q of getFeedQueries()) {
      const data = q.state.data as SocialFeedResponse | undefined;
      if (!data || !Array.isArray(data.data)) continue;
      const next = data.data.map((p) => (p.id === postId ? updater(p) : p));
      if (next.some((p, i) => p !== data.data[i])) {
        q.setState({ data: { ...data, data: next } });
      }
    }
    invalidateFeed();
  };

  /** Restore the snapshot after a failed mutation. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const restoreSnapshot = (snap: any) => {
    if (!snap) return;
    for (const [key, data] of snap) {
      const q = qc.getQueryCache().getAll().find(
        (q) => JSON.stringify(q.queryKey) === JSON.stringify(key),
      );
      if (q && data !== undefined) q.setState({ data });
    }
  };

  /** Drop a post from all cached feed queries (delete). */
  const removePostFromCache = (postId: number) => {
    for (const q of getFeedQueries()) {
      const data = q.state.data as SocialFeedResponse | undefined;
      if (!data || !Array.isArray(data.data)) continue;
      const next = data.data.filter((p) => p.id !== postId);
      if (next.length !== data.data.length) {
        q.setState({ data: { ...data, data: next } });
      }
    }
    invalidateFeed();
  };

  /** Invalidate all social queries so the next background refetch
   *  picks up authoritative data from the server. */
  const invalidateFeed = () =>
    qc.invalidateQueries({ queryKey: socialKeys.all });



  const comments = commentsByPost[post.id] || [];
  const hasMoreComments = commentsHasMoreByPost[post.id] ?? false;
  const loadingComments = isLoadingComments[post.id] ?? false;

  // Permission flags. We defend against missing `post.author` so a
  // malformed post object doesn't crash the whole card (this was
  // a real crash — "Cannot read properties of undefined (reading
  // 'id')" — visible in production console). All comparisons
  // short-circuit when the author or current user is missing.
  const authorId = (post as any)?.author?.id;
  const isAuthor = authorId != null && (currentUser as any)?.id === authorId;
  const userRoles = (currentUser as any)?.roles || [];
  const isAdmin = userRoles.some((r: string) =>
    ['admin', 'ADMIN', 'ROLE_ADMIN', 'SUPER_ADMIN'].includes(r)
  );
  const canDelete = isAuthor || isAdmin;
  const visMeta = VISIBILITY_META[post.visibility] || VISIBILITY_META.PUBLIC;
  const VisIcon = visMeta.icon;
  // Defensive defaults. A backend response that lacks `content`
  // (e.g. a stale cached payload) would otherwise crash on
  // `.length`. We coerce to '' so the rest of the card keeps
  // working — the user just sees an empty post body.
  const safeContent = typeof post.content === 'string' ? post.content : '';
  const safeMedia = Array.isArray(post.media) ? post.media : [];
  const safePoll = post.poll ?? null;
  const safeLikesCount = typeof post.likesCount === 'number' ? post.likesCount : 0;
  const safeCommentsCount = typeof post.commentsCount === 'number' ? post.commentsCount : 0;
  const safeSavesCount = typeof post.savesCount === 'number' ? post.savesCount : 0;
  const safeIsLiked = !!post.isLiked;
  const safeIsSaved = !!post.isSaved;
  // Ở chế độ chi tiết (modal bình luận / deep-link) người đọc đã chủ động
  // mở đúng bài này — cắt ngang rồi bắt bấm "Xem thêm" là một bước thừa,
  // và với bài dài của loạt "100 Ngày Java" thì mốc 600 ký tự còn rơi vào
  // giữa một khối code.
  const contentLong = !detailMode && safeContent.length > MAX_PREVIEW_LENGTH;
  const visibleContent = expanded || !contentLong ? safeContent : previewOf(safeContent);

  // Cleanup the long-press timer on unmount so we don't leak
  useEffect(() => {
    return () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
    };
  }, []);

  const handleToggleComments = () => {
    // Detail mode (inside the modal): the list is already open and
    // the modal owns the composer — don't recursively open a modal.
    if (detailMode) return;
    // Facebook-style: the comment button opens the dedicated comment
    // modal (centered dialog on desktop, full-screen sheet on mobile)
    // instead of expanding inline. The modal loads the comments.
    playPop(); // one clean sound on open, like Facebook
    useSocialStore.getState().openCommentModal(post.id);
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() && !commentMedia) return;

    setIsSubmitting(true);
    const submittedText = commentText;
    const submittedMedia = commentMedia;
    const tempId = Date.now();
    const optimisticComment: SocialComment = {
      id: tempId,
      postId: post.id,
      content: submittedText,
      mediaUrl: submittedMedia?.url ?? null,
      mediaKind: submittedMedia?.kind ?? null,
      likesCount: 0,
      repliesCount: 0,
      isEdited: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      user: {
        id: 0,
        username: 'You',
        fullName: 'You',
        avatarUrl: null,
      },
      isLiked: false,
      replies: [],
    };
    addOptimisticComment(post.id, optimisticComment);
    // Snapshot mention ids BEFORE clearing the textarea so the
    // createComment payload carries them to the server. Without
    // this the server never knows who was @'d and no NEW_MENTION
    // notification fires.
    const mentionsArr = Array.from(commentMentions);
    setCommentText('');
    setCommentMentions(new Set());
    setCommentMedia(null);
    setCommentPicker(null);

    try {
      await socialApi.createComment({
        postId: post.id,
        content: submittedText,
        mentions: mentionsArr.length > 0 ? mentionsArr : undefined,
        mediaUrl: submittedMedia?.url,
        mediaKind: submittedMedia?.kind,
      });
    } catch {
      // optimistic already added, ignore for now
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Repost toggle (Phase 6) ─────────────────────────────────
  // Snapshot the feed cache + Zustand before mutation so we can
  // roll back on error.
  const handleRepost = async () => {
    setShowShareMenu(false);
    const wasShared = (post as any).isShared ?? false;
    const willBeShared = !wasShared;
    const currentSharesCount = (post as any).sharesCount ?? 0;
    const willBeSharesCount = willBeShared ? currentSharesCount + 1 : Math.max(0, currentSharesCount - 1);

    // Snapshot TQ cache + Zustand for rollback
    const snapshot = snapshotFeed();

    // Optimistic UI — patch TQ cache
    patchFeed(post.id, (p) => ({ ...p, isShared: willBeShared, sharesCount: willBeSharesCount }));
    // Optimistic UI — patch Zustand
    useSocialStore.setState((s) => ({
      posts: s.posts.map((p) =>
        p.id === post.id ? { ...p, isShared: willBeShared, sharesCount: willBeSharesCount } : p
      ),
    }));

    try {
      const res = await socialApi.sharePost(post.id);
      const data = (res as any)?.data?.data;
      // Server confirms the new state
      const nowShared = data?.shared ?? willBeShared;
      patchFeed(post.id, (p) => ({ ...p, isShared: nowShared }));
      useSocialStore.setState((s) => ({
        posts: s.posts.map((p) =>
          p.id === post.id ? { ...p, isShared: nowShared } : p
        ),
      }));
      toast.success(nowShared ? 'Đã đăng lại' : 'Đã huỷ đăng lại');
    } catch (err: any) {
      // Roll back on error
      restoreSnapshot(snapshot);
      useSocialStore.setState((s) => ({
        posts: s.posts.map((p) =>
          p.id === post.id ? { ...p, isShared: wasShared, sharesCount: currentSharesCount } : p
        ),
      }));
      toast.error(err?.response?.data?.message || 'Không thể đăng lại');
    }
  };

  const handleShare = (platform: string) => {
    setShowShareMenu(false);
    if (platform === 'copy') {
      navigator.clipboard.writeText(`${window.location.origin}/?post=${post.id}`);
      toast.success('Đã sao chép liên kết');
      return;
    }
    if (platform === 'repost') {
      void handleRepost();
      return;
    }
    void socialApi.sharePost(post.id, platform);
    const urls: Record<string, string> = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(safeContent.slice(0, 100))}&url=${encodeURIComponent(window.location.origin + '/?post=' + post.id)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin + '/?post=' + post.id)}`,
    };
    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'noopener');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Bạn có chắc muốn xoá bài viết này?')) return;
    // Snapshot both TQ cache and Zustand for rollback on error.
    const snapshot = snapshotFeed();
    const prevPosts = useSocialStore.getState().posts;
    const prevSaved = useSocialStore.getState().savedPosts;

    // ─── Optimistic UI — update BOTH stores immediately so the card
    // disappears INSTANTLY without waiting for the network round-trip.
    removePostFromCache(post.id);
    useSocialStore.setState((s) => ({
      posts: s.posts.filter((p) => p.id !== post.id),
      savedPosts: s.savedPosts.filter((p) => p.id !== post.id),
    }));

    try {
      if (onDelete) {
        await onDelete(post.id);
      } else {
        await deletePost(post.id);
      }
      toast.success('Đã xoá bài viết');
      setShowMoreMenu(false);
    } catch (err: any) {
      // Restore both TQ cache AND Zustand on failure.
      restoreSnapshot(snapshot);
      useSocialStore.setState({ posts: prevPosts, savedPosts: prevSaved });
      toast.error(err?.response?.data?.message || 'Xoá thất bại');
    }
  };

  const handleLikeComment = async (commentId: number) => {
    try {
      await socialApi.likeComment(commentId);
    } catch {
      // ignore
    }
  };

  /**
   * Delete a comment. We hit the backend directly here (not through
   * the store) because the optimistic list update is bound to the
   * specific PostCard instance — refetching the whole comment list
   * on every delete would re-trigger the cursor pagination.
   */
  const handleDeleteComment = async (commentId: number) => {
    if (!confirm('Xoá bình luận này?')) return;
    try {
      await socialApi.deleteComment(commentId);
      setShowComments(false);
      // Refresh feed to update comment count
      loadComments(post.id, true);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Xoá bình luận thất bại');
    }
  };

  /**
   * Post a reply to a top-level comment.
   *   - parentId: id of the comment being replied to
   *   - content: the full text (including the leading
   *     "@display " that CommentItem pre-filled)
   *   - mentions: optional user ids that the reply composer
   *     collected from MentionAutocomplete's onPick. We send
   *     them in the createComment payload so the server can
   *     fan out NEW_MENTION notifications to the right people.
   */
  const handleReplyComment = async (
    parentId: number,
    content: string,
    mentions?: number[],
  ) => {
    const mentionIds = (mentions ?? []).filter(
      (n) => Number.isFinite(n) && n > 0,
    );

    try {
      await socialApi.createComment({
        postId: post.id,
        parentId,
        content,
        mentions: mentionIds.length > 0 ? mentionIds : undefined,
      });
      // Refresh the comment list so the new reply shows up
      // under the parent.
      loadComments(post.id, true);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Gửi trả lời thất bại');
      throw err; // let CommentItem re-enable its submit button
    }
  };

  // Phase 5 home upgrade: lazy-load replies beyond the first
  // page. Called from CommentItem when the user clicks
  // "Xem thêm N phản hồi". We just delegate to the API; the
  // caller (CommentItem) merges the result into its local
  // `extraReplies` state.
  const handleLoadMoreReplies = async (rootId: number, cursor: number): Promise<SocialComment[] | null> => {
    try {
      const res = await socialApi.getCommentReplies(rootId, { cursor, limit: 10 });
      const data = (res.data as unknown as { data: SocialComment[] }).data ?? [];
      return data;
    } catch (err) {
      toast.error('Không tải được thêm phản hồi');
      return null;
    }
  };

  // Long-press to show reactions panel
  const startLongPress = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => setShowReactions(true), 450);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // ─── Multi-emoji reactions (added 2026-06-20) ──────────────────
  // Click on the picker (long-press or hover) → call
  // `toggleReaction(type)`. The store handles optimistic update
  // and the round-trip; this is just a thin proxy.
  //
  // We ALSO patch the TanStack Query cache in parallel because
  // the /social page renders from the cache, not the Zustand
  // store. Without this the emoji never appears on the card
  // even though the API call succeeds.
  const handleReact = (type: ReactionType) => {
    setShowReactions(false);

    // Snapshot for rollback if the API call fails.
    const snapshot = snapshotFeed();

    // ─── Optimistic state update ──────────────────────────────────
    // Compute the next breakdown from the current row, then write
    // it directly to Zustand AND patch the TanStack Query cache
    // so both the Zustand-driven render AND any TQ-driven render
    // stay in sync.
    const prevBreakdown: ReactionBreakdown = {
      LIKE: post.reactionBreakdown?.LIKE ?? 0,
      LOVE: post.reactionBreakdown?.LOVE ?? 0,
      HAHA: post.reactionBreakdown?.HAHA ?? 0,
      SAD:  post.reactionBreakdown?.SAD  ?? 0,
      ANGRY:post.reactionBreakdown?.ANGRY?? 0,
    };
    const prevMyType: ReactionType | null = (post as any).myReaction ?? null;
    const willRemove = prevMyType === type;
    const nextBreakdown: ReactionBreakdown = { ...prevBreakdown };
    if (prevMyType) nextBreakdown[prevMyType] = Math.max(0, nextBreakdown[prevMyType] - 1);
    if (!willRemove) nextBreakdown[type] = (nextBreakdown[type] ?? 0) + 1;
    const nextLikesCount = Object.values(nextBreakdown).reduce((a, b) => a + b, 0);
    const nextMyType: ReactionType | null = willRemove ? null : type;

    // 1. Patch the TQ cache (covers TQ-driven renders). Zustand is
    //    handled EXCLUSIVELY by toggleReaction below — patching it here
    //    too caused a double count: toggleReaction reads the current
    //    store state (already incremented) and incremented again, so the
    //    number jumped by 2 until the server response corrected it.
    patchFeed(post.id, (p) => ({
      ...p,
      myReaction: nextMyType,
      isLiked: !!nextMyType,
      likesCount: nextLikesCount,
      reactionBreakdown: nextBreakdown,
    }));

    // 2. Fire the store action — it owns the Zustand optimistic update,
    //    the API call, and the server-truth reconcile. On failure it
    //    reverts Zustand itself; we only roll back the TQ cache here.
    toggleReaction(post.id, type).catch(() => {
      restoreSnapshot(snapshot);
    });
  };

  // ─── Save toggle (added 2026-06-20) ────────────────────────────
  // The page renders from Zustand AND TQ cache. We must update
  // BOTH so the bookmark icon flips immediately regardless of which
  // store the current page reads from.
  const handleSave = async () => {
    const snapshot = snapshotFeed();
    const wasSaved = post.isSaved;
    const willBeSaved = !wasSaved;
    const nextSavesCount = Math.max(0, (post.savesCount ?? 0) + (wasSaved ? -1 : 1));

    // 1. Patch TQ cache
    patchFeed(post.id, (p) => ({
      ...p,
      isSaved: willBeSaved,
      savesCount: nextSavesCount,
    }));

    // 2. Update Zustand directly
    useSocialStore.setState((s) => ({
      posts: s.posts.map((p) =>
        p.id === post.id ? { ...p, isSaved: willBeSaved, savesCount: nextSavesCount } : p
      ),
    }));

    try {
      if (onToggleSave) {
        await onToggleSave(post.id);
      } else {
        await toggleSave(post.id);
      }
    } catch {
      restoreSnapshot(snapshot);
      // Revert Zustand
      useSocialStore.setState((s) => ({
        posts: s.posts.map((p) =>
          p.id === post.id ? { ...p, isSaved: wasSaved, savesCount: Math.max(0, nextSavesCount + (wasSaved ? 1 : -1)) } : p
        ),
      }));
    }
  };

  // The viewer's current reaction. Drives the heart icon
  // colour + the label below the count.
  const myReaction = (post as any).myReaction as ReactionType | null | undefined;
  const reactionColor = myReaction ? REACTION_META[myReaction].color : '#94a3b8';
  const reactionEmoji = myReaction ? REACTION_META[myReaction].emoji : null;
  const breakdown = (post as any).reactionBreakdown as
    | Record<ReactionType, number>
    | undefined;
  // Count of "non-zero" reaction types so we can show the
  // emoji stack: a post with only 👍 shows just 👍, but a post
  // with 👍 + ❤️ shows both.
  const activeReactions = REACTION_KEYS.filter(
    (k) => (breakdown?.[k] ?? 0) > 0,
  );

  // ─── Saved Collections: handlers (added 2026-06-20) ────────────
  // When the popover opens for the first time, fetch the user's
  // existing collections. We cache the result for the rest of the
  // session so navigating to /saved and back is instant. The list
  // is fetched via the new /feed/collections endpoint; legacy
  // /social/saves/folders still works as a fallback so we don't
  // break if the new route hasn't been deployed yet.
  const ensureCollectionsLoaded = async () => {
    if (collectionsLoaded) return;
    try {
      const res = await socialApi.listCollections();
      const data = res.data?.data;
      if (data && Array.isArray(data.collections)) {
        setCachedCollections(data.collections as SaveCollection[]);
        setCollectionsLoaded(true);
      }
    } catch {
      try {
        // Fallback to legacy endpoint
        const res = await socialApi.getSaveFolders();
        const folders = (res.data?.data?.folders ?? []) as SaveCollection[];
        setCachedCollections(folders);
        setCollectionsLoaded(true);
      } catch {
        // ignore — empty list will be shown
      }
    }
  };

  /**
   * Called by SocialSavePopover when the user picks a collection
   * or hits "Bỏ lưu". We delegate to the existing `toggleSave`
   * action from the social store so the optimistic UI, the
   * `savedFolder` field on the post, and the saves count all stay
   * consistent with the rest of the app.
   *
   * - `folder === null` + `remove === false`: save into the
   *   uncategorised bucket (legacy `toggleSave(id)` semantics).
   * - `folder === 'X'` + `remove === false`: save into "X".
   * - `remove === true`: unsave entirely.
   */
  const handleSaveCommit = async (folder: string | null, remove?: boolean) => {
    // Optimistic cache patch — the popover always saves INTO a
    // folder (or removes). We patch `isSaved` + `savesCount` so
    // the bookmark icon reflects the action immediately.
    const snapshot = snapshotFeed();
    const wasSaved = post.isSaved;
    patchFeed(post.id, (p) => ({
      ...p,
      isSaved: remove ? false : true,
      savedFolder: remove ? null : folder ?? p.savedFolder ?? null,
      savesCount: Math.max(0, (p.savesCount ?? 0) + ((remove || wasSaved) ? -1 : 1)),
    }));
    try {
      if (remove) {
        await (onToggleSave ? onToggleSave(post.id) : toggleSave(post.id));
      } else {
        await toggleSave(post.id, folder ?? undefined);
      }
    } catch (err: any) {
      restoreSnapshot(snapshot);
      toast.error(err?.response?.data?.message || 'Lưu bài viết thất bại');
    }
  };

  // ─── Saved Collections V2 handlers (added 2026-06-20) ────────
  // Lazy-fetch the user's collections + the post's current
  // save context. We re-fetch every time the popover opens
  // so the popover is always fresh (counts, new folders
  // created in another tab, etc.).
  const ensureV2Loaded = async () => {
    setCollectionsV2Loading(true);
    try {
      const [listRes, ctxRes] = await Promise.all([
        socialApi.listCollectionsV2(),
        socialApi.getPostSaveContext(post.id),
      ]);
      const list = (listRes as any)?.data?.data;
      const ctx = (ctxRes as any)?.data?.data;
      if (Array.isArray(list?.collections)) {
        setCollectionsV2(list.collections);
      }
      if (ctx) setSaveContext(ctx);
      setCollectionsV2Loaded(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Tải bộ sưu tập thất bại');
    } finally {
      setCollectionsV2Loading(false);
    }
  };

  /**
   * Multi-collection commit. The popover tells us the new
   * set of collection ids the user wants this post in; we
   * hit `/feed/save-post-v2`, patch the TQ cache + Zustand,
   * and refresh the context.
   */
  const handleCommitV2 = async (collectionIds: number[]) => {
    const snapshot = snapshotFeed();
    const prevPosts = useSocialStore.getState().posts;
    const prevSaved = useSocialStore.getState().savedPosts;
    const wasSaved = post.isSaved;
    const willBeSaved = collectionIds.length > 0;
    const prevSavesCount = post.savesCount ?? 0;
    const nextSavesCount = wasSaved === willBeSaved
      ? prevSavesCount
      : willBeSaved ? prevSavesCount + 1 : Math.max(0, prevSavesCount - 1);

    // ─── Optimistic UI — update TQ cache AND Zustand immediately
    patchFeed(post.id, (p) => ({
      ...p,
      isSaved: willBeSaved,
      savesCount: nextSavesCount,
    }));
    useSocialStore.setState((s) => ({
      posts: s.posts.map((p) =>
        p.id === post.id ? { ...p, isSaved: willBeSaved, savesCount: nextSavesCount } : p
      ),
      savedPosts: willBeSaved
        ? (() => {
            const without = s.savedPosts.filter((p) => p.id !== post.id);
            return [post, ...without];
          })()
        : s.savedPosts.filter((p) => p.id !== post.id),
    }));

    try {
      const res = await socialApi.savePostToCollections(post.id, collectionIds);
      const data = (res as any)?.data?.data as FeedSaveResult | undefined;

      // Refresh save context from server.
      const ctxRes = await socialApi.getPostSaveContext(post.id);
      const freshCtx = (ctxRes as any)?.data?.data as FeedPostSaveContext | undefined;
      if (freshCtx) setSaveContext(freshCtx);

      // Refresh collections list with updated counts.
      const listRes = await socialApi.listCollectionsV2();
      const listData = (listRes as any)?.data?.data;
      if (Array.isArray(listData?.collections)) {
        setCollectionsV2(listData.collections);
      }
    } catch (err: any) {
      // Revert both TQ cache AND Zustand on failure.
      restoreSnapshot(snapshot);
      useSocialStore.setState({ posts: prevPosts, savedPosts: prevSaved });
      toast.error(err?.response?.data?.message || 'Lưu bài viết thất bại');
      throw err;
    }
  };

  /** Inline-create a collection. The new row is appended to
   *  the local list so the popover shows it immediately. */
  const handleCreateV2 = async (name: string): Promise<FeedCollection | null> => {
    try {
      const res = await socialApi.createCollectionV2(name);
      const created = (res as any)?.data?.data as FeedCollection | undefined;
      if (!created) return null;
      // Optimistic prepend. Server returns id + sortOrder.
      setCollectionsV2((prev) => {
        if (prev.some((c) => c.id === created.id)) return prev;
        return [...prev, created];
      });
      return created;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Tạo bộ sưu tập thất bại');
      return null;
    }
  };

  /** Open the V2 popover. Refreshes the data each time. */
  const openSavePopoverV2 = async () => {
    setShowSavePopoverV2(true);
    void ensureV2Loaded();
  };

  // Bookmark button click: open the V2 multi-collection popover.
  // The popover's own toggleCollection → handleCommitV2 path manages
  // all optimistic UI and API calls. We no longer delegate to
  // onToggleSave prop here — PostCard owns the entire save UX.
  const handleSaveClick = async () => {
    if (showSavePopoverV2) {
      setShowSavePopoverV2(false);
      return;
    }
    await openSavePopoverV2();
  };

  // Author info can be missing on a malformed post (e.g. an
  // optimistic update that hasn't received the server echo yet).
  // We always render *something* safe so the rest of the card
  // doesn't crash. The previous code assumed `post.author` was
  // present, which produced the "Cannot read properties of
  // undefined (reading 'id')" runtime error.
  const authorObj = (post as any)?.author ?? null;
  const authorAvatar = authorObj?.avatarUrl
    ? authorObj.avatarUrl
    : `https://api.dicebear.com/7.x/avataaars/svg?seed=${authorObj?.username ?? 'user'}`;
  const authorDisplay =
    authorObj?.displayName || authorObj?.fullName || authorObj?.username || 'Người dùng';

  return (
    <article
      // Phase 4 perf follow-up: dropped the previous inline
      // `backdropFilter: 'blur(20px)'` + `boxShadow: '0 8px 32px ...'`
      // styles. Backdrop-filter was forcing the browser to re-blur
      // the area behind every card on every frame the descendants
      // painted — and with a `<video>` inside the card, that's
      // ~60 re-blurs per second per card, which dropped video
      // playback to ~10 FPS on mid-range laptops.
      // The card keeps the same visual feel via the `.post-card-frame`
      // utility class (see globals.css) which uses a cheap inset
      // box-shadow that lives entirely on the GPU compositor.
      //
      // Phase 6 — overflow + z-index: when ANY menu is open
      // (share / save / reactions / more) we need the dropdowns
      // to escape the card boundary (so they overlay the NEXT
      // post instead of being clipped). Switching to
      // `overflow-visible` is the simplest fix. The rounded
      // corners stay intact because:
      //   • The card's `border-radius` rounds the visible
      //     background, which is fine — children that overflow
      //     just show against the page background.
      //   • The `.post-card-frame` inset box-shadow lives on the
      //     GPU compositor and is independent of overflow.
      //
      // We also bump the z-index of THIS post card so its
      // dropdowns always sit above sibling cards' content
      // (otherwise two posts with dropdowns at the same z-40
      // would each be clipped by the OTHER's overflow:hidden).
      className={cn(
        // Mobile: edge-to-edge (no side rounding/border, thin top+bottom
        // dividers) like the Facebook feed. ≥sm: rounded bordered card.
        'post-card-frame group relative rounded-none border-y border-x-0 border-[color:var(--border-color)] sm:rounded-3xl sm:border-x',
        // Default: clip overflow so rounded corners + shadows stay clean.
        // Open menu: escape the card boundary so dropdowns can
        // overlay the NEXT post in the feed.
        (showShareMenu || showMoreMenu || showReactions || showSavePopover || showSavePopoverV2 || showMessengerPicker)
          ? 'overflow-visible z-30'
          : 'overflow-hidden',
      )}
      // data-post-id lets the home page deep-link from a
      // notification (?post=N) to a specific card via querySelector
      // + scrollIntoView. Bounded: zero perf cost, the value is
      // already on the post object.
      data-post-id={post.id}
      style={{
        background: 'var(--bg-card)',
      }}
    >
      {/* Top accent line */}
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(139,92,246,0.4), rgba(6,182,212,0.4), transparent)',
        }}
      />

      {/* Background-music player — FB/IG-style, at the TOP of the post.
          Renders for ANY post with a musicTrack (incl. text-only posts).
          Plays inline; see PostMusicPlayer. */}
      {post.musicTrack && (
        <PostMusicPlayer
          track={post.musicTrack}
          startSec={post.musicStartSec ?? 0}
          endSec={post.musicEndSec ?? null}
        />
      )}

      <div className="p-5">
        {/* Author row */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {/* Avatar — clicking goes to the public profile. Wrapped
                in a Link so the entire avatar+name cluster is one
                affordance; we add a `stopPropagation` so the card
                body itself doesn't get a click-through. */}
            <Link
              href={isAuthor ? '/profile' : `/profile/${authorId ?? ''}`}
              className="relative h-11 w-11 flex-shrink-0 overflow-hidden rounded-full ring-2 ring-violet-500/20 transition-transform hover:scale-105"
              aria-label={`Xem trang cá nhân của ${authorDisplay}`}
            >
              <SmartImage
                src={authorAvatar}
                alt={authorDisplay}
                loading="lazy"
                decoding="async"
                width={44}
                height={44}
                className="h-full w-full object-cover"
              />
            </Link>

            {/* Name + time + visibility */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Link
                  href={isAuthor ? '/profile' : `/profile/${authorId ?? ''}`}
                  className="font-semibold text-text-primary truncate hover:underline"
                  title={authorDisplay}
                >
                  {authorDisplay}
                </Link>
                {isAdmin && !isAuthor && (
                  <span
                    className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md"
                    style={{ background: 'rgba(234,179,8,0.15)', color: '#facc15', border: '1px solid rgba(234,179,8,0.3)' }}
                  >
                    Admin
                  </span>
                )}
                {/* Huy hiệu "AI" cho MỌI bài của bot bản tin (mạng lưới
                    nhiều bot AI). Nhận diện bằng post.newsCategory != null —
                    mỗi bài bot luôn được gắn một chủ đề (claude / codex /
                    gemini / antigravity / tech / ai-compare). Minh bạch rằng
                    bài do AI tạo; kèm một chip chủ đề nhỏ. */}
                {post.newsCategory != null && (
                  <>
                    <span
                      className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md"
                      style={{ background: 'rgba(79,91,213,0.18)', color: '#a5b4fc', border: '1px solid rgba(79,91,213,0.4)' }}
                      title="Bài viết do AI tạo tự động"
                    >
                      🤖 AI
                    </span>
                    {categoryLabel(post.newsCategory) && (
                      <span
                        className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md"
                        style={{
                          background: hexToRgba(categoryAccent(post.newsCategory), 0.14),
                          color: categoryAccent(post.newsCategory),
                          border: `1px solid ${hexToRgba(categoryAccent(post.newsCategory), 0.4)}`,
                        }}
                        title={`Bản tin: ${categoryLabel(post.newsCategory)}`}
                      >
                        {categoryLabel(post.newsCategory)}
                      </span>
                    )}
                  </>
                )}
                {/* Content-type badge — only for Video/File so normal
                    posts (and non-social callers that don't set `type`)
                    render exactly as before. */}
                {(post.type === 'VIDEO' || post.type === 'FILE') && (
                  <span
                    className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md"
                    style={
                      post.type === 'VIDEO'
                        ? { background: 'rgba(139,92,246,0.15)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.3)' }
                        : { background: 'rgba(6,182,212,0.12)', color: '#67e8f9', border: '1px solid rgba(6,182,212,0.3)' }
                    }
                  >
                    {post.type === 'VIDEO' ? 'Video' : 'File'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5 text-xs flex-wrap" style={{ color: 'var(--text-muted)' }}>
                <span>@{authorObj?.username ?? 'user'}</span>
                <span style={{ color: '#334155' }}>·</span>
                <span>{formatRelative(post.createdAt)}</span>
                <span style={{ color: '#334155' }}>·</span>
                <span className="inline-flex items-center gap-0.5" style={{ color: visMeta.color }} title={visMeta.label}>
                  <VisIcon className="h-3 w-3" />
                </span>
                {post.locationName && (
                  <>
                    <span style={{ color: '#334155' }}>·</span>
                    <span>📍 {post.locationName}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* More menu */}
          <div className="relative flex-shrink-0">
            <button
              className="rounded-xl p-2 transition-colors"
              style={{ color: 'var(--text-muted)' }}
              onClick={(e) => {
                e.stopPropagation();
                setShowMoreMenu(!showMoreMenu);
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-surface-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <MoreHorizontal size={18} />
            </button>
            <AnimatePresence>
              {showMoreMenu && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowMoreMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -5 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -5 }}
                    className="absolute right-0 top-full z-50 mt-1 w-44 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl py-1"
                    style={{
                      background: 'var(--bg-overlay)',
                      border: '1px solid var(--border-light)',
                      backdropFilter: 'blur(20px)',
                      boxShadow: 'var(--shadow-xl)',
                    }}
                  >
                    <button
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                      onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/?post=${post.id}`); setShowMoreMenu(false); toast.success('Đã sao chép liên kết'); }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--text-primary)';
                        e.currentTarget.style.background = 'var(--bg-surface-hover)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--text-secondary)';
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" /> Sao chép liên kết
                    </button>
                    {canDelete && (
                      <button
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors"
                        style={{ color: '#ef4444' }}
                        onClick={handleDelete}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {isAuthor ? 'Xoá bài viết' : 'Xoá (quyền admin)'}
                      </button>
                    )}
                    {!isAuthor && (
                      <button
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors"
                        style={{ color: 'var(--text-secondary)' }}
                        onClick={() => { toast.info('Đã gửi báo cáo'); setShowMoreMenu(false); }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = 'var(--text-primary)';
                          e.currentTarget.style.background = 'var(--bg-surface-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'var(--text-secondary)';
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <Flag className="h-3.5 w-3.5" /> Báo cáo
                      </button>
                    )}

                    {/* Phase 3: Interested */}
                    {!isAuthor && (
                      <button
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors"
                        style={{ color: 'var(--text-secondary)' }}
                        onClick={async () => {
                          setShowMoreMenu(false);
                          try {
                            await api.post(`/social/posts/${post.id}/feedback`, { type: 'INTERESTED' });
                            toast.success('Cảm ơn phản hồi của bạn');
                          } catch {
                            toast.error('Không thể gửi phản hồi');
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = 'var(--text-primary)';
                          e.currentTarget.style.background = 'var(--bg-surface-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'var(--text-secondary)';
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <Flag className="h-3.5 w-3.5" /> Quan tâm
                      </button>
                    )}

                    {/* Phase 3: Save */}
                    {!isAuthor && (
                      <button
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors"
                        style={{ color: 'var(--text-secondary)' }}
                        onClick={() => { setShowMoreMenu(false); }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = 'var(--text-primary)';
                          e.currentTarget.style.background = 'var(--bg-surface-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'var(--text-secondary)';
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <Bookmark className="h-3.5 w-3.5" /> Lưu bài viết
                      </button>
                    )}

                    {/* Phase 3: Subscribe */}
                    {!isAuthor && (
                      <button
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors"
                        style={{ color: 'var(--text-secondary)' }}
                        onClick={async () => {
                          setShowMoreMenu(false);
                          try {
                            await api.post(`/social/posts/${post.id}/subscribe`);
                            toast.success('Đã bật thông báo');
                          } catch {
                            toast.error('Không thể bật thông báo');
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = 'var(--text-primary)';
                          e.currentTarget.style.background = 'var(--bg-surface-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'var(--text-secondary)';
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <Bell className="h-3.5 w-3.5" /> Bật thông báo
                      </button>
                    )}

                    {/* Phase 3: Pin */}
                    {isAuthor && (
                      <button
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors"
                        style={{ color: 'var(--text-secondary)' }}
                        onClick={async () => {
                          setShowMoreMenu(false);
                          try {
                            await api.post(`/social/posts/${post.id}/pin`);
                            toast.success('Đã ghim bài viết');
                          } catch {
                            toast.error('Không thể ghim');
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = 'var(--text-primary)';
                          e.currentTarget.style.background = 'var(--bg-surface-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'var(--text-secondary)';
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <Pin className="h-3.5 w-3.5" /> Ghim bài viết
                      </button>
                    )}

                    {/* Phase 3: Archive */}
                    {isAuthor && (
                      <button
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors"
                        style={{ color: 'var(--text-secondary)' }}
                        onClick={async () => {
                          setShowMoreMenu(false);
                          try {
                            await api.post(`/social/posts/${post.id}/archive`);
                            toast.success('Đã chuyển vào lưu trữ');
                          } catch {
                            toast.error('Không thể lưu trữ');
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = 'var(--text-primary)';
                          e.currentTarget.style.background = 'var(--bg-surface-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'var(--text-secondary)';
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <Archive className="h-3.5 w-3.5" /> Lưu vào kho lưu trữ
                      </button>
                    )}

                    {/* Phase 3: Hide */}
                    {!isAuthor && (
                      <button
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors"
                        style={{ color: 'var(--text-secondary)' }}
                        onClick={async () => {
                          setShowMoreMenu(false);
                          try {
                            await api.post(`/social/posts/${post.id}/hide`);
                            toast.success('Đã ẩn bài viết');
                          } catch {}
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = 'var(--text-primary)';
                          e.currentTarget.style.background = 'var(--bg-surface-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'var(--text-secondary)';
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <EyeOff className="h-3.5 w-3.5" /> Ẩn bài viết
                      </button>
                    )}

                    {/* Phase 3: Snooze */}
                    {!isAuthor && (
                      <button
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors"
                        style={{ color: 'var(--text-secondary)' }}
                        onClick={async () => {
                          setShowMoreMenu(false);
                          const authorId = (post as any)?.author?.id;
                          if (!authorId) return;
                          try {
                            await api.post(`/users/${authorId}/snooze`);
                            toast.success('Đã tạm ẩn bài từ người này trong 30 ngày');
                          } catch {
                            toast.error('Không thể tạm ẩn');
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = 'var(--text-primary)';
                          e.currentTarget.style.background = 'var(--bg-surface-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'var(--text-secondary)';
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <Clock className="h-3.5 w-3.5" /> Tạm ẩn 30 ngày
                      </button>
                    )}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Content with "See more" */}
        <div className="mt-3">
          <RenderContentWithCode content={visibleContent} structureFrom={safeContent} />
          {contentLong && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-1 text-xs font-medium text-neon-violet hover:text-neon-indigo transition-colors"
            >
              {expanded ? 'Thu gọn' : 'Xem thêm'}
            </button>
          )}
        </div>

        {/* Poll */}
        {safePoll && <PostPoll postId={post.id} poll={safePoll} />}

        {/* Media — on mobile it breaks out of the card's p-5 gutter so
            images/video fill the full screen width (Facebook-style);
            ≥sm it stays inside the padded card. */}
        {safeMedia.length > 0 && (
          <div className="-mx-5 sm:mx-0">
            <MediaGrid
              media={safeMedia}
              postId={post.id}
              onOpenTheater={onOpenTheater}
              musicTrack={post.musicTrack ?? null}
              musicStartSec={post.musicStartSec ?? null}
            />
          </div>
        )}

        {/* YouTube embed — shown after media if a URL is attached.
            Renders the official youtube-nocookie iframe so the post
            works with strict privacy settings. */}
        {post.youtubeUrl && (
          <YouTubeEmbed url={post.youtubeUrl} />
        )}

        {/* Action bar — placed BELOW the post content (after the
            image / YouTube embed) so it doesn't overlay the media.
            The previous overlay design blocked carousel swipe gestures
            in the top zone of the image, which broke Next/Prev
            navigation on every post that had an image. */}
        <PostActionsBar
          myReaction={myReaction}
          reactionColor={reactionColor}
          reactionEmoji={reactionEmoji}
          safeLikesCount={safeLikesCount}
          handleReact={handleReact}
          cancelLongPress={cancelLongPress}
          showReactions={showReactions}
          setShowReactions={setShowReactions}
          REACTION_PICKER_ORDER={REACTION_PICKER_ORDER}
          activeReactions={activeReactions}
          showComments={commentsOpen}
          safeCommentsCount={safeCommentsCount}
          handleToggleComments={handleToggleComments}
          handleShare={handleShare}
          handleRepost={handleRepost}
          isShared={(post as any).isShared ?? false}
          sharesCount={(post as any).sharesCount ?? 0}
          showShareMenu={showShareMenu}
          setShowShareMenu={setShowShareMenu}
          showMessengerPicker={showMessengerPicker}
          setShowMessengerPicker={setShowMessengerPicker}
          safeIsSaved={safeIsSaved}
          safeSavesCount={safeSavesCount}
          saveButtonRef={saveButtonRef}
          handleSaveClick={handleSaveClick}
          post={post}
          showSavePopover={showSavePopover}
          setShowSavePopover={setShowSavePopover}
          cachedCollections={cachedCollections}
          handleSaveCommit={handleSaveCommit}
          showSavePopoverV2={showSavePopoverV2}
          setShowSavePopoverV2={setShowSavePopoverV2}
          collectionsV2={collectionsV2}
          saveContext={saveContext}
          collectionsV2Loading={collectionsV2Loading}
          handleCommitV2={handleCommitV2}
          handleCreateV2={handleCreateV2}
          overlay={false}
        />

        {/* Comments section — use grid-template-rows for an
            animated height without triggering layout per frame.
            Animating height: 'auto' forces a synchronous layout
            on every frame; the grid-rows trick lets the browser
            interpolate between 0fr and 1fr in the compositor. */}
        <AnimatePresence initial={false}>
          {commentsOpen && (
            <motion.div
              initial={{ opacity: 0, gridTemplateRows: '0fr' }}
              animate={{ opacity: 1, gridTemplateRows: '1fr' }}
              exit={{ opacity: 0, gridTemplateRows: '0fr' }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="mt-4 grid"
            >
              <div className="min-h-0 overflow-hidden">
              <div
                className="space-y-3"
                style={{ borderTop: '1px solid var(--border-color)', paddingTop: '12px' }}
              >
                {loadingComments && comments.length === 0 ? (
                  <CommentSkeleton />
                ) : comments.length === 0 ? (
                  <p className="py-2 text-center text-xs" style={{ color: '#475569' }}>
                    Chưa có bình luận nào. Hãy là người đầu tiên!
                  </p>
                ) : (
                  <>
                    {comments.map((comment) => {
                      const isCommentAuthor = (currentUser as any)?.id === comment.user?.id;
                      const canDeleteComment = isCommentAuthor || isAdmin;
                      return (
                        <CommentItem
                          key={comment.id}
                          comment={comment}
                          onLike={() => handleLikeComment(comment.id)}
                          onLikeComment={handleLikeComment}
                          onDelete={canDeleteComment ? () => handleDeleteComment(comment.id) : undefined}
                          onDeleteComment={handleDeleteComment}
                          onReply={handleReplyComment}
                          canDelete={canDeleteComment}
                          postId={post.id}
                          // Phase 5 home upgrade: lazy-load more
                          // replies when the server says there
                          // are more than the eagerly fetched
                          // count.
                          onLoadMoreReplies={handleLoadMoreReplies}
                        />
                      );
                    })}
                    {hasMoreComments && (
                      <button
                        className="w-full rounded-xl py-2 text-xs font-medium transition-colors"
                        style={{ color: '#8B5CF6' }}
                        onClick={() => loadMoreComments(post.id)}
                      >
                        Xem thêm bình luận
                      </button>
                    )}
                  </>
                )}

                {/* Comment input — hidden in detail mode; the comment
                    modal supplies its own pinned composer (CommentComposer). */}
                {!detailMode && (
                <form onSubmit={handleSubmitComment} className="relative mt-3 flex items-center gap-2 min-w-0">
                  {/* Media pickers — anchored above the composer (self-position) */}
                  <EmojiPickerPopover
                    open={commentPicker === 'emoji'}
                    onClose={() => setCommentPicker(null)}
                    onPick={(e) => setCommentText((t) => t + e)}
                    anchorRef={commentBarRef}
                  />
                  <GifPicker
                    open={commentPicker === 'gif'}
                    onClose={() => setCommentPicker(null)}
                    onPick={(url) => { setCommentMedia({ url, kind: 'gif' }); setCommentPicker(null); }}
                    anchorRef={commentBarRef}
                  />
                  <StickerPicker
                    open={commentPicker === 'sticker'}
                    onClose={() => setCommentPicker(null)}
                    onPick={(url) => { setCommentMedia({ url, kind: 'sticker' }); setCommentPicker(null); }}
                    anchorRef={commentBarRef}
                  />
                  <div
                    className="flex flex-1 flex-col gap-2 rounded-2xl px-4 py-2.5 min-w-0"
                    style={{
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-color)',
                    }}
                  >
                    {commentMedia && (
                      <div className="relative w-fit">
                        <img
                          src={commentMedia.url}
                          alt={commentMedia.kind}
                          className="max-h-28 w-auto max-w-[160px] rounded-lg object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => setCommentMedia(null)}
                          className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-[11px] leading-none text-white"
                          aria-label="Bỏ media"
                        >
                          ×
                        </button>
                      </div>
                    )}
                    <div ref={commentBarRef} className="flex items-center gap-2 min-w-0">
                      <input
                        ref={commentInputRef}
                        type="text"
                        placeholder="Write a comment... (type @ to tag)"
                        value={commentText}
                        onChange={(e) => {
                          const next = e.target.value;
                          setCommentText(next);
                          // If the user cleared the text, drop the
                          // accumulated mention set too — the next
                          // comment starts from a clean slate.
                          if (next === '') setCommentMentions(new Set());
                        }}
                        className="flex-1 bg-transparent text-sm outline-none min-w-0"
                        style={{ color: 'var(--text-primary)' }}
                      />
                      <button
                        type="button"
                        onClick={() => setCommentPicker((p) => (p === 'emoji' ? null : 'emoji'))}
                        className="flex-shrink-0 text-base leading-none opacity-80 hover:opacity-100"
                        title="Emoji"
                      >
                        😊
                      </button>
                      <button
                        type="button"
                        onClick={() => setCommentPicker((p) => (p === 'gif' ? null : 'gif'))}
                        className="flex-shrink-0 rounded px-1 text-[11px] font-bold opacity-80 hover:opacity-100"
                        style={{ color: '#8B5CF6' }}
                        title="GIF"
                      >
                        GIF
                      </button>
                      <button
                        type="button"
                        onClick={() => setCommentPicker((p) => (p === 'sticker' ? null : 'sticker'))}
                        className="flex-shrink-0 text-base leading-none opacity-80 hover:opacity-100"
                        title="Nhãn dán"
                      >
                        🏷️
                      </button>
                      <button
                        type="submit"
                        disabled={(!commentText.trim() && !commentMedia) || isSubmitting}
                        className="flex-shrink-0 rounded-xl p-1.5 transition-all disabled:opacity-40"
                        style={{ background: 'rgba(139,92,246,0.2)', color: '#8B5CF6' }}
                      >
                        <Send size={14} />
                      </button>
                    </div>
                  </div>
                  {/* Phase 5 home upgrade: @mention autocomplete for the
                      comment composer. Listens to the input's caret
                      and pops up a list of users to tag while they
                      type @username. */}
                  <MentionAutocomplete
                    textareaRef={commentInputRef}
                    value={commentText}
                    onChange={setCommentText}
                    onPick={(item) => {
                      // Append (de-duped) the picked user id to the
                      // mentions set. handleSubmitComment reads
                      // this set when building the createComment
                      // payload, so the server can fan out
                      // NEW_MENTION notifications.
                      setCommentMentions((prev) => {
                        if (prev.has(item.id)) return prev;
                        const next = new Set(prev);
                        next.add(item.id);
                        return next;
                      });
                    }}
                    offsetY={36}
                  />
                </form>
                )}
              </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </article>
  );
}

// ─── Memoised export ────────────────────────────────────────────
// We compare only the surface fields the user actually sees: the
// post body (id, content, media, author), the toggle booleans
// (liked/saved/pinned/visibility), and the counter + updatedAt
// fields. Comment lists + reaction breakdowns live in their own
// sub-components and are owned by the card. Callbacks compare by
// reference — they're wrapped in useCallback at the page level
// (see page.tsx handleToggleLike etc.) so identity stability is
// guaranteed across re-renders of the same feed page.
function postCardPropsEqual(prev: PostCardProps, next: PostCardProps): boolean {
  const a = prev.post;
  const b = next.post;
  if (a === b) return true;
  if (a.id !== b.id) return false;
  if (a.content !== b.content) return false;
  if (a.updatedAt !== b.updatedAt) return false;
  if (a.likesCount !== b.likesCount) return false;
  if (a.commentsCount !== b.commentsCount) return false;
  if (a.savesCount !== b.savesCount) return false;
  if (a.isLiked !== b.isLiked) return false;
  if (a.isSaved !== b.isSaved) return false;
  if (a.visibility !== b.visibility) return false;
  if (a.author?.id !== b.author?.id) return false;
  if (a.author?.avatarUrl !== b.author?.avatarUrl) return false;
  if (a.author?.displayName !== b.author?.displayName) return false;
  // Callback identity — parent must useCallback these.
  if (prev.onToggleLike !== next.onToggleLike) return false;
  if (prev.onToggleSave !== next.onToggleSave) return false;
  if (prev.onDelete !== next.onDelete) return false;
  if (prev.onOpenTheater !== next.onOpenTheater) return false;
  if (prev.detailMode !== next.detailMode) return false;
  return true;
}

export interface PostCardHandle {
  /** Open the comments section, load the comments list, and
   *  scroll to the matching comment. Used by the home page's
   *  ?comment=N deep-link effect — the bell notification click
   *  flow that the user reported wasn't taking them to the
   *  right comment before. */
  openComment: (commentId: number) => void;
}

// PostCardImpl has a long list of internal useState / useRef /
// useEffect that we don't want to duplicate. We use a thin
// forwardRef wrapper that captures the imperative handle and
// delegates rendering to the existing memoised component. The
// wrapper is not memoised because it accepts a ref whose identity
// changes every render — React handles the ref forward without
// re-running PostCardImpl.
const PostCardForward = forwardRef<PostCardHandle, PostCardProps>(
  function PostCardForward(props, ref) {
    return <PostCardWithHandle ref={ref} {...props} />;
  },
);

export const PostCard = memo(PostCardForward, postCardPropsEqual);

function PostCardWithHandle({
  ref,
  ...props
}: PostCardProps & { ref: React.ForwardedRef<PostCardHandle> }) {
  // We need access to setShowComments and loadComments — both
  // are local to the original PostCardImpl. Instead of refactoring
  // that component (large blast radius), we walk the DOM to find
  // the comment we want and click the "Bình luận" toggle if the
  // comments section isn't open yet. The "openComment" imperative
  // does this synchronously after the page has placed the card
  // into the viewport via ?post=N — at that point the card is
  // already mounted and the comments toggle button is in the
  // tree. This is the minimum-risk integration.
  useImperativeHandle(
    ref,
    () => ({
      openComment: (commentId: number) => {
        const postId = props.post.id;
        const card = document.querySelector<HTMLElement>(
          `[data-post-id="${postId}"]`,
        );
        if (!card) return;
        // Click the Bình luận toggle if comments are not yet
        // visible. We use the data-comments-toggle attribute we
        // set on the ActionButton; this is a stable selector that
        // doesn't depend on the button's rendered aria-label text.
        const toggle = card.querySelector<HTMLButtonElement>(
          '[data-comments-toggle="1"]',
        );
        if (toggle) {
          // Check if comments are already visible by looking for
          // any mounted [data-comment-id] inside the card.
          const alreadyOpen = card.querySelector('[data-comment-id]');
          if (!alreadyOpen) toggle.click();
        }
        // Wait two frames for the comments list to mount, then
        // scroll to the target comment and add a brief highlight.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const target = document.querySelector<HTMLElement>(
              `[data-comment-id="${commentId}"]`,
            );
            if (target) {
              target.scrollIntoView({ behavior: 'smooth', block: 'center' });
              target.classList.add(
                'ring-2',
                'ring-violet-500/60',
                'rounded-2xl',
                'transition',
              );
              window.setTimeout(() => {
                target.classList.remove(
                  'ring-2',
                  'ring-violet-500/60',
                  'rounded-2xl',
                );
              }, 2400);
            }
          });
        });
      },
    }),
    [props.post.id],
  );
  return <PostCardImpl {...props} />;
}

// ─── Fullscreen Video Player ──────────────────────────────────────────────────

function VideoPlayerModal({ src, onClose }: { src: string; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerContainerRef = useRef<HTMLDivElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const hideControlsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Pause EVERY inline feed video while this modal is open, and keep
  // them paused until it closes. We can't rely on the src-diff check
  // the inline videos do for `social:video-playing`, because the modal
  // almost always plays the SAME source as the inline video that opened
  // it — so that check would let the inline copy keep playing (the
  // "both videos play / double audio" bug). A dedicated open/close
  // signal pauses them unconditionally and lets in-view ones resume
  // only after the modal closes.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('social:video-modal-open'));
    return () => {
      window.dispatchEvent(new CustomEvent('social:video-modal-close'));
    };
  }, []);

  // Close on Escape (global so it fires even without focus)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Track browser fullscreen state
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Focus player on mount so keyboard shortcuts work immediately
  useEffect(() => {
    playerContainerRef.current?.focus();
  }, []);

  const resetHideTimer = () => {
    setShowControls(true);
    if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
    hideControlsTimer.current = setTimeout(() => setShowControls(false), 2000);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); } else { v.pause(); }
    resetHideTimer();
  };

  // Show controls always when paused
  const handlePause = () => {
    setPlaying(false);
    setShowControls(true);
    if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    setVolume(val);
    if (videoRef.current) { videoRef.current.volume = val; videoRef.current.muted = val === 0; }
    setMuted(val === 0);
  };

  const toggleFullscreen = () => {
    const el = playerContainerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      el.requestFullscreen().catch(() => {});
    }
  };

  // Keyboard shortcuts: Space = play/pause, ←/→ = seek ±5s
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const v = videoRef.current;
    if (!v) return;
    if (e.key === ' ' || e.key === 'k') {
      e.preventDefault();
      togglePlay();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      v.currentTime = Math.max(0, v.currentTime - 5);
      setCurrentTime(v.currentTime);
      resetHideTimer();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      v.currentTime = Math.min(duration, v.currentTime + 5);
      setCurrentTime(v.currentTime);
      resetHideTimer();
    }
  };

  const fmt = (t: number) => {
    if (!t || isNaN(t)) return '0:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95"
      onClick={onClose}
      onMouseMove={resetHideTimer}
    >
      <div
        ref={playerContainerRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className="relative flex h-full w-full max-w-5xl flex-col items-center justify-center outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Video */}
        <video
          ref={videoRef}
          src={src}
          className="max-h-[calc(100dvh-80px)] w-full object-contain cursor-pointer"
          autoPlay
          loop
          playsInline
          onClick={togglePlay}
          onPlay={() => setPlaying(true)}
          onPause={handlePause}
          onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime ?? 0)}
          onLoadStart={() => setVideoError(false)}
          onLoadedMetadata={() => setDuration(videoRef.current?.duration ?? 0)}
          onWaiting={() => setIsBuffering(true)}
          onStalled={() => setIsBuffering(true)}
          onPlaying={() => { setIsBuffering(false); setVideoError(false); }}
          onCanPlay={() => setIsBuffering(false)}
          onError={() => { setVideoError(true); setIsBuffering(false); }}
          onVolumeChange={() => {
            const v = videoRef.current;
            if (!v) return;
            setVolume(v.muted ? 0 : v.volume);
            setMuted(v.muted);
          }}
        />

        {/* Buffering spinner (centered) while the modal video waits for data. */}
        {isBuffering && !videoError && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/55 backdrop-blur-sm">
              <Loader2 className="h-7 w-7 animate-spin text-white" />
            </span>
          </div>
        )}

        {/* Playback error → retry. */}
        {videoError && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center"
            onClick={(e) => {
              e.stopPropagation();
              const v = videoRef.current;
              if (v) { setVideoError(false); setIsBuffering(true); v.load(); v.play().catch(() => {}); }
            }}
          >
            <RotateCcw className="h-8 w-8 text-white" />
            <span className="text-sm font-medium text-white">Không tải được video — chạm để thử lại</span>
          </div>
        )}

        {/* Controls overlay */}
        <AnimatePresence>
          {showControls && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="absolute bottom-0 left-0 right-0 px-4 pb-4 pt-10"
              style={{
                background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)',
              }}
            >
              {/* Scrubber: invisible input on top of visual track */}
              <div className="group/scrub relative mb-3 w-full cursor-pointer py-2">
                {/* Track */}
                <div className="absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-white/25">
                  {/* Fill */}
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-violet-500"
                    style={{ width: `${progress}%` }}
                  />
                  {/* Thumb (appears on hover) */}
                  <div
                    className="pointer-events-none absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 scale-0 rounded-full bg-white shadow-md transition-transform duration-150 group-hover/scrub:scale-100"
                    style={{ left: `calc(${progress}% - 7px)` }}
                  />
                </div>
                <input
                  type="range"
                  min={0}
                  max={duration || 100}
                  step={0.1}
                  value={currentTime}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    if (videoRef.current) videoRef.current.currentTime = val;
                    setCurrentTime(val);
                    resetHideTimer();
                  }}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  aria-label="Seek"
                />
              </div>

              {/* Bottom controls row */}
              <div className="flex items-center gap-3 text-white">
                {/* Play/Pause */}
                <button onClick={togglePlay} className="shrink-0 transition-opacity hover:opacity-75" aria-label={playing ? 'Pause' : 'Play'}>
                  {playing ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                    </svg>
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                  )}
                </button>

                {/* Time */}
                <span className="text-[13px] tabular-nums opacity-80">
                  {fmt(currentTime)} / {fmt(duration)}
                </span>

                <div className="flex-1" />

                {/* Volume */}
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => {
                      if (videoRef.current) {
                        videoRef.current.muted = !videoRef.current.muted;
                        setMuted(videoRef.current.muted);
                      }
                    }}
                    className="transition-opacity hover:opacity-75"
                    aria-label={muted || volume === 0 ? 'Unmute' : 'Mute'}
                  >
                    {muted || volume === 0 ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                        <line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                      </svg>
                    )}
                  </button>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={muted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="w-20 accent-violet-500 cursor-pointer"
                    aria-label="Volume"
                  />
                </div>

                {/* Fullscreen toggle */}
                <button onClick={toggleFullscreen} className="ml-1 transition-opacity hover:opacity-75" aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
                  {isFullscreen ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M8 3v3a2 2 0 0 1-2 2H3" /><path d="M21 8h-3a2 2 0 0 1-2-2V3" />
                      <path d="M3 16h3a2 2 0 0 1 2 2v3" /><path d="M16 21v-3a2 2 0 0 1 2-2h3" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" />
                      <path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                    </svg>
                  )}
                </button>

                {/* Close */}
                <button onClick={onClose} className="ml-1 transition-opacity hover:opacity-75" aria-label="Close">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Top close button (always visible) */}
        <button
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full bg-black/50 p-1.5 text-white transition-colors hover:bg-black/80"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </motion.div>
  );
}

// ─── Media Grid ───────────────────────────────────────────────────────────────

function MediaGrid({
  media,
  postId,
  onOpenTheater,
  musicTrack,
  musicStartSec,
}: {
  media: SocialMedia[];
  /** Owning post id — passed down so MediaItem's Theater button
   *  can fire the right callback without re-deriving it. */
  postId?: number;
  onOpenTheater?: (postId: number) => void;
  // Phase 3 add — Instagram-style music sticker. When set,
  // the FIRST media tile gets the sticker overlay. We only
  // render it on the first tile to avoid cluttering multi-image
  // carousels (Instagram itself does the same).
  musicTrack?: MusicTrackMini | null;
  musicStartSec?: number | null;
}) {
  const router = useRouter();
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const visual = media.filter((m) => m.type !== 'FILE');
  const files = media.filter((m) => m.type === 'FILE');
  // Feed video posts render in a tall FB/TikTok-style frame (~90% of the
  // screen height) instead of a tiny collapsed cell. Scoped to posts
  // whose visual media are ALL videos so image posts keep their layout.
  const allVideos = visual.length > 0 && visual.every((m) => m.type === 'VIDEO');

  // ── Landscape-video fix (2026-07-02) ────────────────────────────────
  // The tall frame above is right for PORTRAIT videos, but a LANDSCAPE
  // video letterboxed inside it shows two huge black bars above/below
  // (object-contain in a ~880px-tall box). We measure each video's
  // intrinsic ratio (server width/height metadata when present, else
  // the <video>'s loadedmetadata / poster's naturalWidth) and, when the
  // active tile is landscape or square, size the frame to the video's
  // OWN aspect ratio instead of forcing the tall frame. Portrait keeps
  // the TikTok-style frame unchanged.
  const [measuredAspects, setMeasuredAspects] = useState<Record<number, number>>({});
  const reportAspect = (idx: number, ratio: number) => {
    if (!ratio || !isFinite(ratio)) return;
    setMeasuredAspects((prev) => (prev[idx] === ratio ? prev : { ...prev, [idx]: ratio }));
  };
  const metaAspect = (m?: SocialMedia) =>
    m && m.width && m.height ? m.width / m.height : null;

  const handleMediaClick = (item: SocialMedia) => {
    const url = getMediaUrl(item.url, item.url);
    if (item.type === 'VIDEO') {
      // Facebook-style: tapping a feed video opens the full-screen
      // vertical Reels player seeded at this post. We carry over the
      // active video-category filter (?vc=) from the current URL so the
      // reel stays scoped to the same category the user was browsing.
      // Falls back to the inline theater modal only when we have no post
      // id to seed the reel with (e.g. detached previews).
      if (postId != null) {
        let href = `/feed/video?start=${postId}`;
        if (typeof window !== 'undefined') {
          const vc = new URLSearchParams(window.location.search).get('vc');
          if (vc) href += `&vc=${vc}`;
        }
        router.push(href);
      } else {
        setVideoSrc(url);
      }
    } else {
      setLightboxSrc(url);
    }
  };


  // Single-image path is a degenerate case of the carousel
  // (no dots, no arrows) so the JSX below handles 1+ uniformly.
  // (Bypasses the grid layout — Instagram itself does the same.)

  // Horizontal swipeable carousel state. We track:
  //  - currentIdx: which tile is in view
  //  - dragOffset: how many px the user is currently dragging —
  //    applied as translate-x on the inner track so the slide
  //    follows the finger 1:1 while dragging. Released via the
  //    snapTo logic below.
  const [currentIdx, setCurrentIdx] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const startXRef = useRef(0);
  const startIdxRef = useRef(0);
  const startYRef = useRef(0);
  const isDraggingRef = useRef(false);
  // Phase 6 — trackpad / mouse-wheel horizontal swipe support.
  // On macOS a two-finger trackpad swipe fires a `wheel` event
  // with deltaX (not a pointer/touch event). We accumulate the
  // deltaX and once it crosses a threshold we advance / rewind
  // the carousel by one slide. wheelLockRef prevents the wheel
  // events from queueing up while the slide transition is still
  // animating (otherwise a single fast swipe would jump several
  // slides at once).
  const wheelAccumRef = useRef(0);
  const wheelLockRef = useRef(false);

  // Keep currentIdx in range when the post is updated (e.g. a
  // new tile added/removed). We clamp instead of resetting so
  // the user keeps their place.
  useEffect(() => {
    if (currentIdx >= visual.length) setCurrentIdx(Math.max(0, visual.length - 1));
  }, [visual.length, currentIdx]);

  // Pointer drag handlers (mouse + touch). We use Pointer Events
  // so the same code handles both — pointer capture means the
  // drag keeps tracking even if the user leaves the track
  // rectangle mid-drag. Threshold: 50px horizontal OR 15% of
  // the track width (whichever is smaller) commits a slide;
  // smaller throws snap back to the current tile.
  //
  // ── Bugfix (2026-06-28) ─────────────────────────────────────────
  // The previous implementation relied on `isDragging` (React
  // state) inside the move/up handlers. State updates are
  // asynchronous, so the FIRST pointermove right after
  // pointerdown saw the stale `false` and bailed out — the
  // carousel would not track the finger until the second
  // move event, which felt broken especially with many tiles
  // (5–6+). We now mirror isDragging into a ref so the
  // handlers always read the latest value.
  const onPointerDown = (e: React.PointerEvent) => {
    if (visual.length <= 1) return;
    // ─── Critical: don't capture the pointer if the user is
    // touching one of the carousel's OWN interactive elements
    // (the prev/next arrows, the dot pager, the counter pill).
    // Capturing a pointer when the click started on one of
    // those buttons REDIRECTS all subsequent pointer events to
    // the carousel wrapper and suppresses the synthetic click
    // event on the button — the user sees a frozen carousel on
    // every Next/Prev click.
    //
    // We match on aria-label / data-testid instead of the
    // generic <button> selector because the IMAGE/VIDEO tiles
    // themselves are wrapped in <button> for tap-to-open, and
    // we DO want to start a drag from the tile body. ───
    const target = e.target as HTMLElement;
    if (
      target.closest('[data-carousel-control="1"]') ||
      target.closest('[aria-label="Ảnh trước"], [aria-label="Ảnh sau"]') ||
      target.closest('[data-testid^="media-dot-"]') ||
      target.closest('a[href]')
    ) {
      return;
    }
    // Only start drag on horizontal intent. A vertical scroll
    // (touch device) should still scroll the page, not the
    // carousel. We bail out if the touch starts with a clear
    // vertical bias — otherwise the carousel would steal
    // scroll gestures on mobile.
    isDraggingRef.current = true;
    setIsDragging(true);
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    startIdxRef.current = currentIdx;
    setDragOffset(0);
    // Capture on the INNER track (the flex container being
    // transformed), not the outer wrapper. The control buttons
    // are siblings of the inner track so their pointer events
    // don't fire here at all — which is exactly what we want.
    const inner = trackRef.current?.querySelector('[data-carousel-track]') as HTMLElement | null;
    try {
      (inner || e.currentTarget).setPointerCapture(e.pointerId);
    } catch {
      /* setPointerCapture can throw on some browsers when the
         pointer is already released; ignore — pointerup still
         fires via the capture target. */
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    // Defensive: if the move somehow originated on one of the
    // carousel's own controls, ignore. (Shouldn't happen now
    // that onPointerDown bails out, but keep this as a safety
    // net.) We use the same selector logic as onPointerDown.
    const target = e.target as HTMLElement;
    if (
      target.closest('[data-carousel-control="1"]') ||
      target.closest('[aria-label="Ảnh trước"], [aria-label="Ảnh sau"]') ||
      target.closest('[data-testid^="media-dot-"]') ||
      target.closest('a[href]')
    ) return;

    const dx = e.clientX - startXRef.current;
    const dy = e.clientY - startYRef.current;
    // If the user is scrolling vertically (dy dominant), let the
    // page handle the scroll and don't translate the carousel.
    // We only commit horizontal drags.
    if (Math.abs(dy) > Math.abs(dx) * 1.5 && Math.abs(dy) > 8) {
      isDraggingRef.current = false;
      setIsDragging(false);
      setDragOffset(0);
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch { /* noop */ }
      return;
    }
    // Rubber-band resistance at the edges: first 25% of the
    // over-drag moves 1:1, the rest is dampened. This makes
    // swiping past the first/last tile feel like a tug instead
    // of a hard wall.
    const trackWidth = trackRef.current?.clientWidth ?? 1;
    const atStart = startIdxRef.current === 0 && dx > 0;
    const atEnd = startIdxRef.current === visual.length - 1 && dx < 0;
    let displayDx = dx;
    if (atStart || atEnd) {
      const overshoot = Math.abs(dx);
      const dampened = Math.min(overshoot * 0.35, trackWidth * 0.18);
      displayDx = dx < 0 ? -dampened : dampened;
    }
    setDragOffset(displayDx);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);
    const dx = e.clientX - startXRef.current;
    const trackWidth = trackRef.current?.clientWidth ?? 1;
    const threshold = Math.min(50, trackWidth * 0.15);
    let next = startIdxRef.current;
    if (dx < -threshold) next = Math.min(visual.length - 1, startIdxRef.current + 1);
    else if (dx > threshold) next = Math.max(0, startIdxRef.current - 1);
    setCurrentIdx(next);
    setDragOffset(0);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch { /* noop */ }
  };

  // Phase 6 — trackpad / mouse-wheel horizontal swipe. Two-finger
  // swipes on macOS trackpads (and middle-button drags on Windows
  // precision touchpads) deliver `wheel` events with deltaX values.
  // We accumulate the delta until it crosses a threshold (~25% of
  // the carousel width) and then advance / rewind by ONE slide. We
  // lock for 350ms after a wheel-triggered advance so a single
  // continuous gesture only registers as a single slide change.
  //
  // We do NOT preventDefault on vertical wheel events — page scroll
  // should still work while the cursor is over the carousel.
  const onWheel = (e: React.WheelEvent) => {
    if (visual.length <= 1) return;
    // Ignore pure vertical scroll so the page can still scroll
    // past the post while the cursor is on the carousel.
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX) * 1.2) return;
    if (wheelLockRef.current) return;
    wheelAccumRef.current += e.deltaX;
    const trackWidth = trackRef.current?.clientWidth ?? 1;
    const threshold = trackWidth * 0.25;
    if (wheelAccumRef.current <= -threshold) {
      wheelLockRef.current = true;
      setCurrentIdx((i) => Math.min(visual.length - 1, i + 1));
      wheelAccumRef.current = 0;
      setTimeout(() => { wheelLockRef.current = false; }, 350);
    } else if (wheelAccumRef.current >= threshold) {
      wheelLockRef.current = true;
      setCurrentIdx((i) => Math.max(0, i - 1));
      wheelAccumRef.current = 0;
      setTimeout(() => { wheelLockRef.current = false; }, 350);
    }
    // preventDefault is intentional — without it the wheel
    // event also triggers the page's vertical scroll, which
    // makes the carousel feel janky.
    e.preventDefault();
  };

  // Tap a tile to open the lightbox/video (Phase 1 behaviour).
  // We keep tap-to-open for the lightbox-style zoom-in and add
  // swipe for the carousel navigation. Tap is detected by
  // pointerdown + pointerup on the same target within a small
  // distance + short time — anything else is a drag.
  const tapStartRef = useRef<{ x: number; y: number; t: number; target: EventTarget | null } | null>(null);

  // Nằm SAU mọi hook của carousel (trước đây đứng trên chúng ⇒ số hook đổi khi bài
  // chuyển giữa "chỉ có file" và "có ảnh/video" ⇒ React error #310).
  if (visual.length === 0) {
    // Pure file post (no image / video). Hand off to the file list
    // renderer below so we don't end up rendering an empty grid.
    return (
      <>
        <FileAttachmentList media={files} />
      </>
    );
  }
  const onTilePointerDown = (e: React.PointerEvent, item: SocialMedia) => {
    tapStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      t: Date.now(),
      target: e.currentTarget,
    };
  };
  const onTilePointerUp = (e: React.PointerEvent, item: SocialMedia) => {
    const start = tapStartRef.current;
    tapStartRef.current = null;
    if (!start) return;
    const dx = Math.abs(e.clientX - start.x);
    const dy = Math.abs(e.clientY - start.y);
    const dt = Date.now() - start.t;
    if (dt > 250 || dx > 6 || dy > 6) return; // it was a drag
    handleMediaClick(item);
  };

  // Phase 3 add — music sticker always rides on the FIRST tile
  // (per Instagram convention). When the user navigates the
  // carousel, the sticker follows the active tile (which is
  // visually always index 0, so it stays put at the bottom).
  // We use `key` on the inner track so framer-motion remounts
  // the active tile cleanly when items change.
  const renderCarousel = () => (
    <div
      ref={trackRef}
      data-testid="media-carousel"
      data-current-idx={currentIdx}
      data-total={visual.length}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
      className="relative w-full overflow-hidden rounded-none sm:rounded-2xl touch-pan-y select-none"
      style={(() => {
        if (!allVideos) return undefined;
        // Active tile's ratio, falling back to the first tile's, then to
        // server metadata. Unknown → keep the tall frame (portrait default).
        const aspect =
          measuredAspects[currentIdx] ?? metaAspect(visual[currentIdx]) ??
          measuredAspects[0] ?? metaAspect(visual[0]) ?? null;
        if (aspect && aspect >= 1) {
          // Landscape / square: frame matches the video exactly — no
          // letterbox bars. maxHeight is a safety net for ultra-wide
          // parents; in the ~600px feed column it never kicks in.
          return { aspectRatio: String(aspect), maxHeight: 'min(88vh, 880px)' };
        }
        if (aspect) {
          // Portrait: frame also tracks the real ratio. When the height
          // cap binds, maxWidth shrinks with it (height × aspect) and the
          // frame centers — otherwise object-contain would leave big black
          // bands above/below 4:5 / 3:4 clips inside a fixed tall box.
          return {
            aspectRatio: String(aspect),
            maxHeight: 'min(88vh, 880px)',
            maxWidth: `calc(min(88vh, 880px) * ${aspect})`,
            marginInline: 'auto',
          };
        }
        // Ratio unknown (metadata not loaded yet): keep the tall frame.
        return { height: 'min(88vh, 880px)' };
      })()}
    >
      <div
        data-carousel-track
        className="flex h-full"
        style={{
          // ── Bugfix (2026-06-28) ─────────────────────────────────
          // The previous track was sized `width: ${N * 100}%` and
          // each tile `${100 / N}%`. translateX(`-${i * 100}%`)
          // then shifted by the TRACK width (one screen × N), not
          // one tile, so for N=5–6+ slides jumped multiple tiles
          // or wrapped. We now keep the track 100% of the parent
          // and each tile 100% of the track, so `-i * 100%`
          // equals exactly one tile width regardless of N.
          // Drag offset stays in pixels (rubber-band dampened
          // above).
          width: '100%',
          transform: `translate3d(calc(${-currentIdx * 100}% + ${dragOffset}px), 0, 0)`,
          transition: isDragging ? 'none' : 'transform 260ms cubic-bezier(0.22, 1, 0.36, 1)',
          willChange: 'transform',
        }}
      >
        {visual.map((item, i) => (
          <div
            key={item.id}
            className="relative shrink-0 grow-0 basis-full"
            onPointerDown={(e) => onTilePointerDown(e, item)}
            onPointerUp={(e) => onTilePointerUp(e, item)}
            onPointerCancel={() => { tapStartRef.current = null; }}
          >
            <MediaItem
              item={item}
              onClick={() => { /* taps handled by pointerup above */ }}
              autoPlayEnabled={item.type === 'VIDEO' && i === currentIdx}
              onAspect={allVideos ? (r) => reportAspect(i, r) : undefined}
              onOpenTheater={
                onOpenTheater && postId != null
                  ? () => onOpenTheater(postId)
                  : undefined
              }
              reelHref={
                item.type === 'VIDEO' && postId != null
                  ? `/feed/video?start=${postId}`
                  : undefined
              }
            />
            {/* Music now plays via the FB/IG-style PostMusicPlayer at the
                top of the post (see PostCard header), not an overlay here. */}
          </div>
        ))}
      </div>

      {/* Side arrows (desktop). Show on hover only so they
          don't crowd the mobile layout. The arrows are 40px
          squares centered vertically on each edge with a
          dark translucent background. */}
      {visual.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
            disabled={currentIdx === 0}
            aria-label="Ảnh trước"
            data-carousel-control="1"
            className="absolute left-2 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center h-9 w-9 rounded-full bg-black/60 text-white transition-opacity hover:bg-black/80 disabled:opacity-0 md:flex"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => setCurrentIdx((i) => Math.min(visual.length - 1, i + 1))}
            disabled={currentIdx === visual.length - 1}
            aria-label="Ảnh sau"
            data-carousel-control="1"
            className="absolute right-2 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center h-9 w-9 rounded-full bg-black/60 text-white transition-opacity hover:bg-black/80 disabled:opacity-0 md:flex"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </>
      )}

      {/* Counter pill (bottom-left) + dots (bottom-center). Both
          match Instagram's overlay style. The counter used to be
          top-right but the floating action bar now occupies the
          top edge, so we moved the counter to bottom-left so it
          doesn't fight for the same corner. Dots stay bottom-center
          as before. Hidden on 1-image posts where they add noise. */}
      {visual.length > 1 && (
        <>
          <div className="pointer-events-none absolute left-2 bottom-2 z-10 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-white shadow">
            {currentIdx + 1}/{visual.length}
          </div>
          <div
            role="tablist"
            aria-label="Carousel position"
            className="pointer-events-auto absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-1.5"
          >
            {visual.map((_, i) => (
              <button
                key={i}
                role="tab"
                aria-selected={i === currentIdx}
                aria-label={`Ảnh ${i + 1}`}
                onClick={() => setCurrentIdx(i)}
                data-testid={`media-dot-${i}`}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  i === currentIdx
                    ? 'w-5 bg-white shadow'
                    : 'w-1.5 bg-white/60 hover:bg-white/80',
                )}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );

  if (visual.length === 1) {
    return (
      <div className="mt-3 relative">
        {renderCarousel()}
        {files.length > 0 && (
          <div className="mt-2">
            <FileAttachmentList media={files} />
          </div>
        )}
        {lightboxSrc && (
          <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
        )}
        <AnimatePresence>
          {videoSrc && (
            <VideoPlayerModal src={videoSrc} onClose={() => setVideoSrc(null)} />
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="mt-3 relative">
      {renderCarousel()}
      {files.length > 0 && (
        <div className="mt-2">
          <FileAttachmentList media={files} />
        </div>
      )}
      {lightboxSrc && (
        <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      )}
      <AnimatePresence>
        {videoSrc && (
          <VideoPlayerModal src={videoSrc} onClose={() => setVideoSrc(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── File Attachment List ─────────────────────────────────────────────
// Renders one row per FILE attachment with a download button. The
// icon + colour are picked from the file extension so a glance is
// enough to tell archives from source files from docs.

function fileIconForPostCard(name: string): { Icon: any; color: string } {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['zip', 'rar', '7z', 'tar', 'gz', 'tgz'].includes(ext)) {
    return { Icon: FileArchive, color: '#f59e0b' };
  }
  if (['md', 'txt', 'log'].includes(ext)) {
    return { Icon: FileText, color: '#94a3b8' };
  }
  if (['pdf'].includes(ext)) {
    return { Icon: FileText, color: '#ef4444' };
  }
  if (['doc', 'docx', 'odt'].includes(ext)) {
    return { Icon: FileText, color: '#3b82f6' };
  }
  if (['xls', 'xlsx', 'csv'].includes(ext)) {
    return { Icon: FileSpreadsheet, color: '#22c55e' };
  }
  if (['js', 'jsx', 'ts', 'tsx', 'json', 'py', 'go', 'rs', 'java', 'rb', 'php', 'css', 'html', 'yml', 'yaml'].includes(ext)) {
    return { Icon: FileCode, color: '#a78bfa' };
  }
  return { Icon: FileText, color: '#64748b' };
}

function humanFileSizePostCard(bytes?: number | string | bigint | null): string {
  if (bytes == null) return '';
  const n = typeof bytes === 'string' ? Number(bytes) : typeof bytes === 'bigint' ? Number(bytes) : bytes;
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function FileAttachmentList({ media }: { media: SocialMedia[] }) {
  return (
    <ul
      className="overflow-hidden rounded-2xl"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-light)' }}
    >
      {media.map((m) => {
        const name = m.fileName || m.alt || 'Tệp đính kèm';
        const { Icon, color } = fileIconForPostCard(name);
        return (
          <li
            key={m.id}
            className="flex items-center gap-3 border-b border-theme-light px-3 py-2.5 last:border-b-0"
          >
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
              style={{ background: `${color}20`, color }}
            >
              <Icon size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-text-primary" title={name}>
                {name}
              </p>
              <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                {humanFileSizePostCard(m.fileSize)}
                {m.mimeType ? ` · ${m.mimeType}` : ''}
              </p>
            </div>
            <a
              href={m.url}
              download={name}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-9 items-center gap-1.5 rounded-lg px-3 text-xs font-medium transition-colors"
              style={{
                background: 'rgba(139,92,246,0.12)',
                color: '#a78bfa',
                border: '1px solid rgba(139,92,246,0.3)',
              }}
              title="Tải xuống"
            >
              <Download size={14} />
              Tải
            </a>
          </li>
        );
      })}
    </ul>
  );
}

function MediaItem({
  item,
  onClick,
  autoPlayEnabled = false,
  onOpenTheater,
  onAspect,
  reelHref,
}: {
  item: SocialMedia;
  onClick: () => void;
  autoPlayEnabled?: boolean;
  onOpenTheater?: () => void;
  /** Reports the media's intrinsic width/height ratio once known —
      used by the all-video frame to size itself to landscape videos. */
  onAspect?: (ratio: number) => void;
  /** Link into the TikTok-style vertical reel seeded at this post. */
  reelHref?: string;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLButtonElement | null>(null);
  const [isInView, setIsInView] = useState(false);
  // Sound is a GLOBAL, persisted preference (Facebook-style): enabling it on
  // one feed video makes every subsequent in-view video play with sound, and
  // the choice survives reloads. `muted` is derived from it.
  const [soundOn, setSoundOn] = useState<boolean>(getFeedSound);
  useEffect(() => subscribeFeedSound(setSoundOn), []);
  const muted = !soundOn;
  // Inline playback state for the control bar
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showInlineControls, setShowInlineControls] = useState(false);
  const inlineHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Flips true once the <video> can actually play; used to retry the
  // autoplay if the first play() call raced ahead of the media loading.
  const [canPlay, setCanPlay] = useState(false);
  // Buffering / error UX: without these the <video> just freezes on a stall
  // (no feedback). onWaiting/onStalled show a centered spinner; onError shows
  // a retry affordance instead of a dead frame.
  const [isBuffering, setIsBuffering] = useState(false);
  const [videoError, setVideoError] = useState(false);
  // True while a VideoPlayerModal is open anywhere on the page. Inline
  // videos must NOT play while a modal is up (only the modal should have
  // sound/motion), and must resume — if still in view — once it closes.
  const [modalOpen, setModalOpen] = useState(false);
  // TikTok rule: a video the USER explicitly paused stays paused — no
  // re-autoplay when it scrolls back into view — until the user presses
  // play again. Only the inline pause button sets this; system pauses
  // (scroll-out, another video starting, a modal opening) must not.
  const userPausedRef = useRef(false);

  // Pause this inline video when another inline video starts playing
  // (only one autoplaying feed video at a time). Skip if the same source
  // re-started (e.g. re-autoplay after a scroll pause).
  useEffect(() => {
    if (!autoPlayEnabled || item.type !== 'VIDEO') return;
    const v = videoRef.current;
    if (!v) return;
    const src = v.currentSrc;
    const handler = (e: Event) => {
      const newSrc = (e as CustomEvent).detail?.src as string | undefined;
      if (newSrc && newSrc !== src) {
        v.pause();
        setIsPlaying(false);
      }
    };
    window.addEventListener('social:video-playing', handler);
    return () => window.removeEventListener('social:video-playing', handler);
  }, [autoPlayEnabled, item.type]);

  // Modal open/close: pause immediately when a fullscreen player opens,
  // regardless of source, and let the isInView effect resume on close.
  useEffect(() => {
    if (item.type !== 'VIDEO') return;
    const onOpen = () => {
      setModalOpen(true);
      const v = videoRef.current;
      if (v) { v.pause(); setIsPlaying(false); }
    };
    const onClose = () => setModalOpen(false);
    window.addEventListener('social:video-modal-open', onOpen);
    window.addEventListener('social:video-modal-close', onClose);
    return () => {
      window.removeEventListener('social:video-modal-open', onOpen);
      window.removeEventListener('social:video-modal-close', onClose);
    };
  }, [item.type]);

  // ─── Auto-play on scroll: only when the video cell is at least
  // 50% visible. We use an IntersectionObserver to avoid expensive
  // scroll-listener math.
  //
  // Phase 4 perf follow-up: simplified from 4 thresholds
  // ([0, 0.3, 0.6, 0.9]) down to a single 0.5 threshold with a
  // 50px rootMargin. The old setup made the playback toggle
  // (play/pause) flip-flop several times per scroll tick whenever
  // a card was near the 60% boundary, which forced video decode
  // to restart constantly — visible as a choppy ~10 FPS playback
  // in the feed. One threshold + rootMargin keeps the toggle
  // hysteresis wide enough to avoid that, while rootMargin gives
  // the video a head-start (play before the user actually
  // centres the card).
  useEffect(() => {
    if (!autoPlayEnabled || item.type !== 'VIDEO') return;
    const el = containerRef.current;
    if (!el) return;
    // "In view" = the video overlaps the vertical CENTRE band of the
    // viewport (the middle ~20%), not a 50% area ratio. A big ~90vh
    // video can be as tall as the viewport — on Safari especially `vh`
    // counts the area under the URL bar — so it may NEVER reach a 0.5
    // area ratio, which left autoplay never firing. A centre-band test
    // (rootMargin shrinks the root to a thin middle strip, threshold 0)
    // is height-independent and matches TikTok "whatever is centred
    // plays". The wide band also gives stable hysteresis (no flip-flop).
    // Band widened -40% → -30% (middle 40% of the viewport): videos
    // start sooner and keep playing longer while the user scrolls,
    // closer to TikTok's "always playing" feel. Still one observer,
    // one threshold — the flip-flop perf fix above is preserved.
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          setIsInView(e.isIntersecting);
        }
      },
      { root: null, rootMargin: '-30% 0px -30% 0px', threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [autoPlayEnabled, item.type]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Play only when the cell is in view, no fullscreen modal is open,
    // AND the user hasn't explicitly paused this video. A user pause
    // "sticks" across scroll (TikTok behaviour) until they press play.
    if (isInView && !modalOpen && !userPausedRef.current) {
      // CRITICAL for TikTok-style autoplay: force the muted *property*
      // (not just the attribute) right before play(). React only sets
      // the `muted` attribute reliably, so the browser can see a
      // not-actually-muted video and BLOCK autoplay — leaving the video
      // paused until the user taps play. Setting the property guarantees
      // muted autoplay is allowed. `playsInline` avoids iOS fullscreen.
      v.muted = muted;
      v.playsInline = true;
      // Upgrade THIS in-view video to eager loading so play() has data
      // (the element defaults to preload="metadata" to spare the rest of
      // the feed). Then start it.
      if (v.preload !== 'auto') v.preload = 'auto';
      // Try to play with the global sound preference. If the browser blocks
      // UNMUTED autoplay (no user gesture yet), fall back to muted playback so
      // the video still plays — sound kicks in on the next video once the
      // user has tapped the speaker once.
      v.play().catch(() => {
        if (!v.muted) {
          v.muted = true;
          v.play().catch(() => {});
        }
      });
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('social:video-playing', { detail: { src: v.currentSrc } }));
      }
    } else {
      v.pause();
      // Leaving the viewport also drops sound (Facebook behaviour): the next
      // time it scrolls in it re-applies the current global sound preference.
      v.muted = true;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('social:video-paused', { detail: { src: v.currentSrc } }));
      }
    }
  }, [isInView, modalOpen, canPlay, muted]);

  const resetInlineHideTimer = () => {
    setShowInlineControls(true);
    if (inlineHideTimer.current) clearTimeout(inlineHideTimer.current);
    inlineHideTimer.current = setTimeout(() => setShowInlineControls(false), 2000);
  };

  const fmtInline = (t: number) => {
    if (!t || isNaN(t)) return '0:00';
    return `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
  };

  const inlineProgress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (item.type === 'VIDEO') {
    return (
      <button
        ref={containerRef}
        onClick={onClick}
        className="group/media relative h-full w-full overflow-hidden bg-black"
        onMouseEnter={resetInlineHideTimer}
        onMouseMove={resetInlineHideTimer}
        onMouseLeave={() => { if (isPlaying) setShowInlineControls(false); }}
      >
        {autoPlayEnabled ? (
          <video
            ref={videoRef}
            src={getMediaUrl(item.url, item.url)}
            poster={item.thumbnail ? getMediaUrl(item.thumbnail, item.thumbnail) : undefined}
            muted={muted}
            loop
            playsInline
            preload="metadata"
            // Phase 4 perf follow-up: promote the video onto its
            // own GPU compositing layer so the browser doesn't
            // re-composite the parent card (which still re-paints
            // periodically — like buttons, comment counts, etc.)
            // every time the video frame changes. Without this
            // hint the video decode and the card repaint end up
            // on the same compositor and contend for the GPU.
            style={{ willChange: 'transform' }}
            className="h-full w-full object-contain"
            onClick={(e) => e.stopPropagation()}
            onPlay={() => setIsPlaying(true)}
            onPause={() => { setIsPlaying(false); setShowInlineControls(true); }}
            onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime ?? 0)}
            onLoadStart={() => setVideoError(false)}
            onLoadedMetadata={() => {
              const v = videoRef.current;
              setDuration(v?.duration ?? 0);
              if (v && v.videoWidth > 0 && v.videoHeight > 0) {
                onAspect?.(v.videoWidth / v.videoHeight);
              }
            }}
            // Buffering feedback: the network can't keep up → show the spinner.
            onWaiting={() => setIsBuffering(true)}
            onStalled={() => setIsBuffering(true)}
            onPlaying={() => { setIsBuffering(false); setVideoError(false); }}
            onError={() => { setVideoError(true); setIsBuffering(false); }}
            // Retry autoplay once the media is actually playable. A
            // play() fired before data is buffered can no-op on some
            // browsers; flipping canPlay re-runs the play effect.
            onCanPlay={() => { setCanPlay(true); setIsBuffering(false); }}
          />
        ) : (
          <img
            src={getMediaUrl(item.thumbnail || item.url, item.thumbnail || item.url)}
            alt={item.alt || ''}
            loading="lazy"
            decoding="async"
            width={600}
            height={600}
            className="h-full w-full object-contain"
            onLoad={(e) => {
              const img = e.currentTarget;
              if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                onAspect?.(img.naturalWidth / img.naturalHeight);
              }
            }}
          />
        )}

        {!autoPlayEnabled && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full"
              style={{ background: 'rgba(0,0,0,0.6)' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        )}

        {/* Buffering spinner — shown while the video stalls waiting for data. */}
        {autoPlayEnabled && isBuffering && !videoError && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/55 backdrop-blur-sm">
              <Loader2 className="h-6 w-6 animate-spin text-white" />
            </span>
          </div>
        )}

        {/* Playback error — offer a retry instead of a frozen frame. */}
        {autoPlayEnabled && videoError && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 px-4 text-center"
            onClick={(e) => {
              e.stopPropagation();
              const v = videoRef.current;
              if (v) { setVideoError(false); setIsBuffering(true); v.load(); v.play().catch(() => {}); }
            }}
          >
            <RotateCcw className="h-7 w-7 text-white" />
            <span className="text-xs font-medium text-white">Không tải được video — chạm để thử lại</span>
          </div>
        )}

        {/* Top-right shortcuts: Reel (vertical fullscreen feed) + Theater */}
        {(onOpenTheater || reelHref) && (
          <div className="absolute top-2 right-2 flex items-center gap-1.5">
            {reelHref && (
              <Link
                href={reelHref}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-[10px] font-semibold text-white backdrop-blur-md transition-colors hover:bg-neon-violet"
                title="Xem dạng Reel (vuốt dọc toàn màn hình)"
                aria-label="Xem dạng Reel"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="7" y="2" width="10" height="20" rx="2.5" />
                  <path d="M11 9.5v5l4-2.5z" fill="currentColor" stroke="none" />
                </svg>
                Reel
              </Link>
            )}
            {onOpenTheater && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenTheater();
                }}
                className="flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-[10px] font-semibold text-white backdrop-blur-md transition-colors hover:bg-neon-violet"
                title="Mở Theater Mode (toàn màn hình)"
                aria-label="Mở Theater Mode"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                  <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
                  <path d="M3 16v3a2 2 0 0 0 2 2h3" />
                  <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                </svg>
                Theater
              </button>
            )}
          </div>
        )}

        {/* Inline control bar (auto-play mode only) */}
        {autoPlayEnabled && (
          <div
            className={`absolute bottom-0 left-0 right-0 px-2 pb-2 pt-6 transition-opacity duration-200 ${showInlineControls ? 'opacity-100' : 'opacity-0'}`}
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Scrubber */}
            <div className="group/iscrub relative mb-1.5 w-full cursor-pointer py-1.5">
              <div className="absolute left-0 right-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-white/25">
                <div className="absolute inset-y-0 left-0 rounded-full bg-violet-500" style={{ width: `${inlineProgress}%` }} />
                <div
                  className="pointer-events-none absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 scale-0 rounded-full bg-white shadow transition-transform duration-150 group-hover/iscrub:scale-100"
                  style={{ left: `calc(${inlineProgress}% - 5px)` }}
                />
              </div>
              <input
                type="range"
                min={0}
                max={duration || 100}
                step={0.1}
                value={currentTime}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (videoRef.current) videoRef.current.currentTime = val;
                  setCurrentTime(val);
                }}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                aria-label="Seek"
              />
            </div>
            {/* Controls row */}
            <div className="flex items-center gap-2 text-white">
              {/* Play/Pause */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const v = videoRef.current;
                  if (!v) return;
                  if (v.paused) {
                    // User resumes → clear the sticky pause so scroll
                    // autoplay works again.
                    userPausedRef.current = false;
                    v.play().catch(() => {});
                  } else {
                    // User pauses → make it stick across scroll.
                    userPausedRef.current = true;
                    v.pause();
                  }
                }}
                className="shrink-0 transition-opacity hover:opacity-75"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                )}
              </button>
              {/* Time */}
              <span className="text-[10px] tabular-nums opacity-80">
                {fmtInline(currentTime)} / {fmtInline(duration)}
              </span>
              <div className="flex-1" />
              {/* Mute — toggles the GLOBAL feed sound preference */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setFeedSound(!soundOn);
                }}
                className="transition-opacity hover:opacity-75"
                aria-label={muted ? 'Unmute' : 'Mute'}
              >
                {muted ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                ) : (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Always-visible sound toggle (Facebook-style) — shown when the full
            control bar is hidden (e.g. on mobile with no hover) so the user
            can enable sound with a single tap. Tapping it is the gesture that
            unlocks unmuted autoplay for every following in-view video. */}
        {autoPlayEnabled && !showInlineControls && (
          <button
            onClick={(e) => { e.stopPropagation(); setFeedSound(!soundOn); }}
            className="absolute bottom-2 right-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white ring-1 ring-white/15 backdrop-blur-sm transition-opacity hover:bg-black/70"
            aria-label={muted ? 'Bật tiếng' : 'Tắt tiếng'}
          >
            {muted ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            )}
          </button>
        )}

        {/* Static duration badge (non-autoplay) */}
        {!autoPlayEnabled && item.duration && (
          <div
            className="absolute bottom-2 right-2 rounded-md px-1.5 py-0.5 text-xs font-medium text-white"
            style={{ background: 'rgba(0,0,0,0.7)' }}
          >
            {formatDuration(item.duration)}
          </div>
        )}
      </button>
    );
  }

  // Post media urls can be:
  //   - a full https URL (R2 public CDN or any other host) → as-is
  //   - an R2 bucket key (no leading slash, no `uploads/`) → CDN
  //   - a relative path starting with `/` → backend origin
  // getMediaUrl() handles all three so legacy local uploads
  // and new R2 uploads both render.
  const imgUrl = getMediaUrl(item.url, item.url) || '';

  return (
    <button
      onClick={(e) => {
        // The carousel detects tap vs drag at the wrapper level
        // and decides whether to advance the slide or open the
        // lightbox. We stopPropagation here so the button's own
        // onClick (which opens the lightbox) doesn't fire when the
        // user is dragging — only when it's a clean tap.
        e.stopPropagation();
        onClick();
      }}
      // Phase 6 — preserve the image's native aspect ratio.
      // Previously the container was `overflow-hidden` and the
      // img was `object-cover`, which CROPPED portraits/landscapes
      // to a square. We now render the img at intrinsic size
      // (width=100% of container, height driven by the image's
      // intrinsic aspect ratio). The container is letterboxed
      // black where the image doesn't fill, and the post card
      // grows vertically to fit the image — same behaviour as
      // Instagram's feed. The carousel's outer overflow-hidden
      // container keeps the rounded corners clean.
      className="relative block w-full overflow-hidden bg-black/40"
    >
      <SmartImage
        src={imgUrl}
        alt={item.alt || ''}
        loading="lazy"
        decoding="async"
        // `block w-full h-auto` = image fills container width,
        // height = natural aspect ratio. `mx-auto` centers the
        // image when the parent is wider than the image (e.g.
        // very-wide landscape images get letterboxed left/right).
        className="block w-full h-auto mx-auto"
        style={{ maxHeight: '80vh', objectFit: 'contain' }}
        // Drag protection: prevent the browser from interpreting
        // the image drag as a file download. We still let the
        // parent wrapper see pointerdown/pointermove for the
        // carousel swipe.
        onDragStart={(e) => e.preventDefault()}
      />
    </button>
  );
}

function Lightbox({ src, onClose }: { src: string; onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="relative max-h-full max-w-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <SmartImage
          src={src}
          alt="Full size"
          className="rounded-2xl"
          style={{ maxHeight: '80vh', objectFit: 'contain', width: 'auto', height: 'auto' }}
        />
        <button
          onClick={onClose}
          className="absolute -right-3 -top-3 flex h-8 w-8 items-center justify-center rounded-full"
          style={{ background: 'rgba(255,255,255,0.1)' }}
        >
          <X size={16} className="text-white" />
        </button>
      </motion.div>
    </motion.div>
  );
}

// ─── Comment Item ─────────────────────────────────────────────────────────────

// Phase 5 home upgrade: signature for the lazy-load-more-replies
// callback. Extracted to a top-level alias so the `Promise<X[]>`
// generic doesn't get parsed as JSX inside the inline object
// type that follows it.
type CommentLoadMoreFn = (rootId: number, cursor: number) => Promise<SocialComment[] | null | undefined>;

function CommentItem({
  comment,
  onLike,
  onLikeComment,
  onDelete,
  onDeleteComment,
  onReply,
  canDelete,
  postId,
  depth = 0,
  onLoadMoreReplies,
}: {
  comment: SocialComment;
  onLike: () => void;
  onLikeComment?: (commentId: number) => void;
  onDelete?: () => void;
  onDeleteComment?: (commentId: number) => void;
  // Optional `mentions` array carries the user ids the user @'d in
  // the reply (collected via MentionAutocomplete.onPick). The parent
  // PostCard forwards them in the createComment payload so the server
  // can fan out NEW_MENTION notifications.
  onReply?: (parentId: number, content: string, mentions?: number[]) => Promise<void>;
  canDelete?: boolean;
  postId: number;
  depth?: number;
  onLoadMoreReplies?: CommentLoadMoreFn;
}) {  // Defensive defaults — a comment without a user object (e.g.
  // one that's been partially deleted on the server) should still
  // render without crashing the whole card.
  const commentUser = (comment as any)?.user ?? {};
  const commentUserId = commentUser?.id;
  const commentUsername = commentUser?.username ?? 'user';
  const avatar = commentUser.avatarUrl
    ? commentUser.avatarUrl
    : `https://api.dicebear.com/7.x/avataaars/svg?seed=${commentUsername}`;
  const display = commentUser.displayName || commentUser.fullName || commentUser.username || 'Người dùng';
  // Cờ Pro do backend tính (isPro && chưa hết hạn) — xem `tacGiaCongKhai`
  // trong social.service.ts. Ở đây KHÔNG tự suy ra từ ngày hết hạn: client
  // và máy chủ lệch giờ thì huy hiệu sẽ nhấp nháy sai quanh lúc hết hạn.
  const commentPro = Boolean((commentUser as { isPro?: boolean }).isPro);

  // Reply input state. We track per-comment whether the input
  // is open, what the user has typed, and whether the
  // submission is in flight. The store doesn't own this state
  // — it's a pure UI concern of CommentItem.
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  // User ids the user @'d in the current reply. Populated by
  // MentionAutocomplete's onPick. Sent in the createComment
  // payload so the server can fan out NEW_MENTION notifications.
  const [replyMentions, setReplyMentions] = useState<Set<number>>(new Set());
  const [replySubmitting, setReplySubmitting] = useState(false);
  // Phase 5 home upgrade: extra replies fetched lazily when the
  // user clicks "Xem thêm N phản hồi". We track them locally so
  // we can append without rebuilding the parent's tree.
  const [extraReplies, setExtraReplies] = useState<SocialComment[]>([]);
  const [loadingMoreReplies, setLoadingMoreReplies] = useState(false);

  // Pre-fill `@display ` when the user clicks the Reply button
  // for the first time. We use the display name with a trailing
  // space so the user can immediately start typing their reply.
  const openReply = () => {
    setReplyText((cur) => (cur.length === 0 ? `@${display} ` : cur));
    setReplyOpen(true);
  };

  const submitReply = async () => {
    const trimmed = replyText.trim();
    if (!trimmed || !onReply) return;
    setReplySubmitting(true);
    try {
      // Snapshot mention ids so the parent's createComment
      // payload carries the right user ids. The mention set
      // is cleared after submit; the next reply starts fresh.
      const mentions = Array.from(replyMentions);
      await onReply(comment.id, trimmed, mentions);
      setReplyText('');
      setReplyOpen(false);
      setReplyMentions(new Set());
    } catch {
      /* parent surfaces the error toast */
    } finally {
      setReplySubmitting(false);
    }
  };

  // Phase 5 home upgrade: ref for the reply input so the
  // MentionAutocomplete dropdown can hook into it. Same pattern
  // as the top-level comment composer.
  const replyInputRef = useRef<HTMLInputElement | null>(null);
  // Phase 5 home upgrade: lazy-load more replies. We track the
  // cursor (last reply id we've shown) so the parent can issue
  // the next page request. The parent passes a callback so this
  // component doesn't need to know about socialApi directly.
  const handleLoadMoreReplies = async () => {
    if (!onLoadMoreReplies) return;
    const allShown = [...(comment.replies ?? []), ...extraReplies];
    const last = allShown[allShown.length - 1];
    if (!last) return;
    setLoadingMoreReplies(true);
    try {
      const next = await onLoadMoreReplies(comment.id, last.id);
      if (next && next.length > 0) {
        setExtraReplies((prev) => [...prev, ...next]);
      }
    } catch {
      // silent — parent can retry via refresh
    } finally {
      setLoadingMoreReplies(false);
    }
  };

  // Render the comment body with @mention chips. We split on
  // @username tokens and wrap each one in a clickable link.
  // The mention user IDs are on `comment.mentions` so we can
  // pick the right href per token.
  const mentionIds: number[] = Array.isArray((comment as any).mentions)
    ? ((comment as any).mentions as number[])
    : [];
  const renderContent = () => {
    // linkifyToNodes turns pasted URLs into clickable links AND keeps
    // the existing @mention styling (mentions: true). @mentions are a
    // visual hint only — clicking a link opens it in a new tab; the
    // click is stopped from bubbling so it doesn't open the post.
    return linkifyToNodes(comment.content || '', { mentions: true });
  };

  return (
    <div
      // data-comment-id is the anchor used by the home page's
      // ?comment=N deep-link effect (see app/page.tsx). Bounded:
      // it's just a DOM attribute, zero perf cost, and only matters
      // when the user navigates from a notification.
      data-comment-id={comment.id}
      className="flex gap-2.5 group"
    >
      <ProAvatarRing isPro={commentPro} size="sm">
        <Link
          href={commentUserId === (useAuthStore.getState().user as any)?.id ? '/profile' : `/profile/${commentUserId ?? ''}`}
          className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-full transition-transform hover:scale-110 block"
        >
          <SmartImage src={avatar} alt={display} loading="lazy" decoding="async" width={32} height={32} className="h-8 w-8 flex-shrink-0 rounded-full object-cover" />
        </Link>
      </ProAvatarRing>
      <div className="flex-1 min-w-0">
        <div
          className="inline-block max-w-full rounded-2xl px-3 py-2"
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-light)' }}
        >
          <span className="inline-flex items-center gap-1.5 flex-wrap">
            <Link
              href={commentUserId === (useAuthStore.getState().user as any)?.id ? '/profile' : `/profile/${commentUserId ?? ''}`}
              className="text-xs font-semibold text-text-primary hover:underline"
            >
              {display}
            </Link>
            <ProTag isPro={commentPro} />
          </span>
          {comment.content ? (
            <p className="mt-0.5 text-sm break-words" style={{ color: 'var(--text-primary)' }}>
              {renderContent()}
            </p>
          ) : null}
          {(comment as any).mediaUrl && (
            <img
              src={(comment as any).mediaUrl}
              alt={(comment as any).mediaKind === 'sticker' ? 'sticker' : 'GIF'}
              loading="lazy"
              className="mt-1 max-h-48 w-auto max-w-[220px] rounded-xl object-contain"
            />
          )}
        </div>
        <div className="mt-1 flex items-center gap-3 pl-1 flex-wrap">
          <button
            onClick={onLike}
            className="flex items-center gap-1 text-xs transition-colors"
            style={{ color: comment.isLiked ? '#ec4899' : '#475569' }}
          >
            <Heart size={12} fill={comment.isLiked ? '#ec4899' : 'none'} />
            {comment.likesCount > 0 && comment.likesCount}
          </button>
          <span className="text-xs" style={{ color: '#334155' }}>
            {formatRelative(comment.createdAt)}
          </span>
          {comment.isEdited && (
            <span className="text-xs" style={{ color: '#334155' }}>
              (đã sửa)
            </span>
          )}
          {/* Reply button — only meaningful for top-level
              comments. We deliberately don't let users reply
              to a reply of a reply (1-level deep, matches the
              backend's 1-level nesting in getComments). */}
          {onReply && depth === 0 && (
            <button
              onClick={openReply}
              className="flex items-center gap-1 text-xs transition-colors text-text-muted hover:text-violet-300"
              title="Trả lời bình luận này"
            >
              <CornerDownRight size={11} />
              Trả lời
            </button>
          )}
          {canDelete && onDelete && (
            <button
              onClick={onDelete}
              className="flex items-center gap-1 text-xs transition-colors text-text-muted hover:text-red-400 opacity-0 group-hover:opacity-100"
              title="Xoá bình luận"
            >
              <Trash2 size={11} />
            </button>
          )}
        </div>

        {/* Reply input — only when replyOpen is true. Indented
            with a left margin so it visually nests under the
            comment. The pre-filled `@display ` token makes it
            obvious to the user who they're replying to. */}
        {replyOpen && (
          <div className="mt-2 ml-2 pl-3 border-l border-violet-500/30">
            <div className="flex items-center gap-2">
              <input
                ref={replyInputRef}
                value={replyText}
                onChange={(e) => {
                  setReplyText(e.target.value);
                  if (e.target.value === '') setReplyMentions(new Set());
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void submitReply();
                  } else if (e.key === 'Escape') {
                    setReplyOpen(false);
                  }
                }}
                autoFocus
                placeholder={`Reply to ${display}... (type @ to tag)`}
                className="flex-1 rounded-xl px-3 py-1.5 text-sm text-text-primary placeholder-text-muted outline-none"
                style={{
                  background: 'var(--bg-surface)',
                  border: '1px solid rgba(139,92,246,0.3)',
                }}
                disabled={replySubmitting}
              />
              <button
                onClick={submitReply}
                disabled={!replyText.trim() || replySubmitting}
                className="p-1.5 rounded-lg transition-colors disabled:opacity-50"
                style={{
                  background: 'rgba(139,92,246,0.2)',
                  color: '#c4b5fd',
                }}
                title="Gửi (Enter)"
              >
                <Send size={12} />
              </button>
              <button
                onClick={() => setReplyOpen(false)}
                className="p-1.5 rounded-lg text-text-muted hover:text-text-secondary"
                title="Huỷ (Esc)"
              >
                <X size={12} />
              </button>
              {/* Phase 5 home upgrade: @mention autocomplete for the
                  reply form. The reply input gets the same dropdown
                  as the top-level composer. onPick pushes the
                  picked user id into replyMentions so submitReply
                  can include it in the createComment payload. */}
              <MentionAutocomplete
                textareaRef={replyInputRef}
                value={replyText}
                onChange={(v) => {
                  setReplyText(v);
                  if (v === '') setReplyMentions(new Set());
                }}
                onPick={(item) => {
                  setReplyMentions((prev) => {
                    if (prev.has(item.id)) return prev;
                    const next = new Set(prev);
                    next.add(item.id);
                    return next;
                  });
                }}
                offsetY={36}
              />
            </div>
          </div>
        )}

        {/* Replies — nested under the top-level comment. We
            only render one level deep (depth==0 → renders its
            .replies[] with depth==1, those don't recurse).
            Phase 5 home upgrade: also append `extraReplies`
            loaded lazily via "Xem thêm" button. */}
        {depth === 0 && (Array.isArray(comment.replies) && comment.replies.length > 0 || extraReplies.length > 0) && (
          <div className="mt-3 ml-3 pl-3 space-y-2 border-l border-theme-light">
            {[...(comment.replies ?? []), ...extraReplies].map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                onLike={() => onLikeComment ? onLikeComment(reply.id) : undefined}
                onLikeComment={onLikeComment}
                onDelete={canDelete ? () => onDeleteComment ? onDeleteComment(reply.id) : undefined : undefined}
                onReply={onReply}
                canDelete={canDelete}
                postId={postId}
                depth={1}
              />
            ))}
            {/* Phase 5 home upgrade: "Xem thêm N phản hồi" — only
                shows when the server signalled there's more than
                what we eagerly fetched. Clicking it calls
                /comments/by-root/:rootId with the last shown
                reply's id as cursor. */}
            {comment.hasMoreReplies && onLoadMoreReplies && (
              <button
                type="button"
                onClick={handleLoadMoreReplies}
                disabled={loadingMoreReplies}
                className="ml-2 flex items-center gap-1 text-xs font-medium text-violet-300 transition-colors hover:text-violet-200 disabled:opacity-50"
              >
                {loadingMoreReplies ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Đang tải…
                  </>
                ) : (
                  <>
                    <CornerDownRight className="h-3 w-3 rotate-180" />
                    Xem thêm {Math.max(0, (comment.repliesCount ?? 0) - (comment.repliesShown ?? comment.replies?.length ?? 0) - extraReplies.length)} phản hồi
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Action Button ───────────────────────────────────────────────────────────

function ActionButton({
  active,
  activeColor,
  icon,
  count,
  label,
  onClick,
}: {
  active: boolean;
  activeColor: string;
  icon: React.ReactNode;
  count?: number;
  label: string;
  onClick: () => void;
}) {
  // Phase 5 home upgrade: iOS-quality motion. Each tap fires a
  // quick scale-down (whileTap) and a counter scale-up
  // (whileHover) so the icon "pops" when the user interacts.
  // The active state animates between inactive/active via the
  // `animate` prop driven by `active` — no manual class
  // swapping, so the colour transition is also spring-eased.
  // This is cheap (no per-frame JS) because the values stay on
  // the compositor.
  return (
    <motion.button
      whileTap={{ scale: 0.88 }}
      whileHover={{ scale: 1.04 }}
      transition={{ type: 'spring', stiffness: 500, damping: 28, mass: 0.6 }}
      animate={{
        color: active ? activeColor : '#64748b',
        backgroundColor: active ? `${activeColor}1A` : 'rgba(0,0,0,0)',
      }}
      className="flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-xs font-medium"
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'var(--bg-surface-hover)';
          e.currentTarget.style.color = 'var(--text-secondary)';
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = '#64748b';
        }
      }}
      onClick={onClick}
      title={label}
    >
      {icon}
      {count != null && count > 0 && <span>{count}</span>}
    </motion.button>
  );
}

// ─── PostActionsBar (rebuilt 2026-06-28) ─────────────────────────────
// Renders the Like / Comment / Repost / Share / Bookmark row as
// ONE rounded "pill" container that can either float over the
// top edge of the post image (when `overlay` is true) or sit
// as a normal block above the comments (when there is no media).
//
// The bookmark button is positioned at the far-right via
// `ml-auto`, while the Like/Comment/Repost/Share cluster sits
// to the left — exactly the new L→R order requested.
//
// We accept every state/callback needed as props (rather than
// importing the parent closure) so the component remains easy
// to swap out or move later. The popover anchors (save popover
// V1 + V2, share menu, reaction picker) are still owned by the
// parent — we only forward the refs that the popovers expect.
function PostActionsBar(props: {
  // Like + reactions
  myReaction: ReactionType | null | undefined;
  reactionColor: string | null | undefined;
  reactionEmoji: string | null | undefined;
  safeLikesCount: number;
  handleReact: (r: ReactionType) => void;
  cancelLongPress: () => void;
  showReactions: boolean;
  setShowReactions: (v: boolean) => void;
  REACTION_PICKER_ORDER: any[];
  activeReactions: ReactionType[];
  // Comment
  showComments: boolean;
  safeCommentsCount: number;
  handleToggleComments: () => void;
  // Share / Repost
  handleShare: (kind: string) => void;
  handleRepost: () => void; // Phase 6 — repost toggle
  isShared: boolean;
  sharesCount: number; // Phase 6 — repost count
  showShareMenu: boolean;
  setShowShareMenu: (v: boolean) => void;
  // Phase 6 — Send-to-Messenger picker state (Instagram-style).
  showMessengerPicker: boolean;
  setShowMessengerPicker: (v: boolean) => void;
  // Bookmark + save popovers
  safeIsSaved: boolean;
  safeSavesCount: number;
  saveButtonRef: React.RefObject<HTMLButtonElement>;
  handleSaveClick: () => void;
  post: SocialPost;
  showSavePopover: boolean;
  setShowSavePopover: (v: boolean) => void;
  cachedCollections: SaveCollection[];
  handleSaveCommit: (folder: string | null, remove?: boolean) => Promise<void>;
  showSavePopoverV2: boolean;
  setShowSavePopoverV2: (v: boolean) => void;
  collectionsV2: FeedCollection[];
  saveContext: FeedPostSaveContext | null;
  collectionsV2Loading: boolean;
  handleCommitV2: (collectionIds: number[]) => Promise<void>;
  handleCreateV2: (name: string) => Promise<FeedCollection | null>;
  // Visual mode
  overlay: boolean;
}) {
  const {
    myReaction,
    reactionColor,
    reactionEmoji,
    safeLikesCount,
    handleReact,
    cancelLongPress,
    showReactions,
    setShowReactions,
    REACTION_PICKER_ORDER,
    activeReactions,
    showComments,
    safeCommentsCount,
    handleToggleComments,
    handleShare,
    handleRepost,
    isShared,
    sharesCount,
    showShareMenu,
    setShowShareMenu,
    showMessengerPicker,
    setShowMessengerPicker,
    safeIsSaved,
    safeSavesCount,
    saveButtonRef,
    handleSaveClick,
    post,
    showSavePopover,
    setShowSavePopover,
    cachedCollections,
    handleSaveCommit,
    showSavePopoverV2,
    setShowSavePopoverV2,
    collectionsV2,
    saveContext,
    collectionsV2Loading,
    handleCommitV2,
    handleCreateV2,
    overlay,
  } = props;

  // Pill container styling. The action bar now sits BELOW the
  // post content (not overlaid on the image), matching the
  // Instagram layout. We render it as a full-width rounded
  // pill with a subtle top divider so it reads as part of
  // the post card while still being visually distinct from
  // the media above. `overlay` is kept for type compatibility
  // but is no longer honoured (always rendered as a block row).
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '8px 8px',
    marginTop: 10,
    marginBottom: 4,
    borderRadius: 9999,
    background: 'var(--bg-glass)',
    border: '1px solid var(--border-light)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  };

  return (
    <>
      <div style={containerStyle} className="select-none min-w-0">
        {/* ─── Like (with hover reaction picker) ──────────── */}
        <div
          className="relative"
          onMouseEnter={() => setShowReactions(true)}
          onMouseLeave={() => {
            cancelLongPress();
            setShowReactions(false);
          }}
        >
          <motion.button
            onClick={() => handleReact('LIKE')}
            whileTap={{ scale: 0.85 }}
            whileHover={{ scale: 1.05 }}
            transition={{ type: 'spring', stiffness: 480, damping: 22 }}
            className="group inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium"
            style={{ color: myReaction && reactionColor ? reactionColor : 'var(--text-secondary)' }}
            aria-label="Thích bài viết"
            aria-pressed={myReaction === 'LIKE'}
          >
            {myReaction ? (
              <motion.span
                key={myReaction}
                initial={{ scale: 0.6, rotate: -12 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 520, damping: 14 }}
                className="text-[15px] leading-none"
                aria-label={REACTION_META[myReaction].label}
              >
                {reactionEmoji}
              </motion.span>
            ) : (
              <Heart size={15} fill="none" className="transition-transform group-active:scale-125" />
            )}
            <span className="tabular-nums">{safeLikesCount}</span>
          </motion.button>
          <AnimatePresence>
            {showReactions && (
              <motion.div
                key="reaction-picker"
                initial={{ opacity: 0, y: 8, scale: 0.85 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.85 }}
                transition={{ type: 'spring', stiffness: 380, damping: 22, mass: 0.6 }}
                className="absolute bottom-full left-0 z-50 flex flex-col items-start pointer-events-auto"
              >
                <div className="w-full cursor-default" style={{ height: '12px', pointerEvents: 'none' }} />
                <div
                  className="relative z-50 flex gap-0.5 rounded-2xl p-1.5 shadow-2xl"
                  style={{
                    background: 'var(--bg-overlay)',
                    border: '1px solid var(--border-light)',
                    backdropFilter: 'blur(20px)',
                  }}
                >
                  {REACTION_PICKER_ORDER.map((k, idx) => {
                    const isWow = k === 'WOW';
                    const r = isWow ? WOW_META : REACTION_META[k as ReactionType];
                    const isMine = !isWow && myReaction === k;
                    return (
                      <motion.button
                        key={k}
                        initial={{ opacity: 0, y: 6, scale: 0.6 }}
                        animate={{
                          opacity: 1,
                          y: 0,
                          scale: 1,
                          transition: { delay: idx * 0.03, type: 'spring', stiffness: 420, damping: 18 },
                        }}
                        exit={{ opacity: 0, scale: 0.6, transition: { duration: 0.1 } }}
                        whileHover={{ scale: 1.45, y: -6, transition: { duration: 0.15 } }}
                        whileTap={{ scale: 1.1 }}
                        onClick={() => {
                          handleReact(isWow ? 'LIKE' : (k as ReactionType));
                        }}
                        className="text-2xl px-1.5 py-0.5 cursor-pointer origin-bottom"
                        title={r.label}
                        style={{
                          transform: isMine ? 'scale(1.18)' : 'scale(1)',
                          filter: isMine ? `drop-shadow(0 0 6px ${r.color})` : undefined,
                        }}
                      >
                        {r.emoji}
                      </motion.button>
                    );
                  })}
                </div>
                {activeReactions.length > 0 && safeLikesCount > 0 && (
                  <div className="flex items-center gap-1 pl-1.5">
                    <div className="flex -space-x-1">
                      {activeReactions.slice(0, 3).map((k) => (
                        <span
                          key={k}
                          className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] leading-none"
                          style={{
                            background: 'var(--bg-overlay)',
                            border: '1px solid var(--border-light)',
                          }}
                          title={REACTION_META[k].label}
                        >
                          {REACTION_META[k].emoji}
                        </span>
                      ))}
                    </div>
                    <span className="text-[10px] text-text-muted tabular-nums">
                      {safeLikesCount}
                    </span>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ─── Comment ─────────────────────────────────────── */}
        <motion.button
          onClick={handleToggleComments}
          whileTap={{ scale: 0.88 }}
          whileHover={{ scale: 1.04 }}
          transition={{ type: 'spring', stiffness: 500, damping: 28, mass: 0.6 }}
          className="group inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium"
          style={{
            color: showComments ? '#a78bfa' : 'var(--text-secondary)',
            background: showComments ? 'rgba(139,92,246,0.16)' : 'transparent',
          }}
          data-comments-toggle="1"
          aria-label="Bình luận"
          aria-pressed={showComments}
          title="Bình luận"
        >
          <MessageCircle size={15} fill={showComments ? '#8B5CF6' : 'none'} />
          <span className="tabular-nums">{safeCommentsCount}</span>
        </motion.button>

        {/* ─── Repost ──────────────────────────────────────── */}
        <motion.button
          onClick={handleRepost}
          whileTap={{ scale: 0.88 }}
          whileHover={{ scale: 1.04 }}
          transition={{ type: 'spring', stiffness: 500, damping: 28, mass: 0.6 }}
          className="group inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium"
          style={{
            color: isShared ? '#22c55e' : 'var(--text-secondary)',
            background: isShared ? 'rgba(34,197,94,0.16)' : 'transparent',
          }}
          aria-label={isShared ? 'Huỷ đăng lại' : 'Đăng lại'}
          title={isShared ? 'Huỷ đăng lại' : 'Đăng lại'}
        >
          <Repeat2 size={15} fill={isShared ? '#22c55e' : 'none'} />
          {sharesCount > 0 && (
            <span className="tabular-nums">{sharesCount}</span>
          )}
        </motion.button>

        {/* ─── Share (opens Send-to-Messenger modal) ─────────── */}
        <div className="relative">
          <motion.button
            onClick={() => {
              setShowShareMenu(false);
              setShowMessengerPicker(true);
              // The SendToMessengerModal lazy-loads the thread
              // list via useMessagingStore on mount — no work
              // needed here.
            }}
            whileTap={{ scale: 0.88 }}
            whileHover={{ scale: 1.04 }}
            transition={{ type: 'spring', stiffness: 500, damping: 28, mass: 0.6 }}
            className="group inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium"
            style={{
              color: showMessengerPicker ? '#22d3ee' : 'var(--text-secondary)',
              background: showMessengerPicker ? 'rgba(6,182,212,0.16)' : 'transparent',
            }}
            aria-label="Gửi qua Messenger"
            aria-haspopup="dialog"
            aria-expanded={showMessengerPicker}
            title="Gửi qua Messenger"
          >
            <Send size={15} />
          </motion.button>
          {/* Phase 6 — keep the legacy copy-link / repost as a small
              dropdown reachable via a long-press / right-click
              escape hatch. Default flow is the Instagram-style
              messenger picker above. */}
          {showShareMenu && (
            <div
              className="absolute right-0 top-full z-50 mt-1 w-48 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl py-1"
              style={{
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border-light)',
                backdropFilter: 'blur(20px)',
              }}
            >
              <button
                className="flex w-full items-center gap-2 px-3 py-2 text-sm"
                style={{ color: 'var(--text-secondary)' }}
                onClick={() => { setShowShareMenu(false); handleShare('copy'); }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
              >
                <Copy size={14} />
                Sao chép liên kết
              </button>
            </div>
          )}
        </div>

        {/* Spacer pushes the bookmark button to the far right */}
        <div className="flex-1" />

        {/* ─── Bookmark (far-right) ────────────────────────── */}
        <motion.button
          ref={saveButtonRef}
          onClick={handleSaveClick}
          whileTap={{ scale: 0.85 }}
          whileHover={{ scale: 1.05 }}
          transition={{ type: 'spring', stiffness: 480, damping: 22 }}
          className="group inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium"
          style={{
            color: safeIsSaved ? NEON_AMBER : 'var(--text-secondary)',
            background: safeIsSaved ? 'rgba(245,158,11,0.16)' : 'transparent',
          }}
          title={safeIsSaved ? `Đã lưu${post.savedFolder ? ` vào "${post.savedFolder}"` : ''}` : 'Lưu bài viết'}
          aria-label={safeIsSaved ? 'Đã lưu bài viết' : 'Lưu bài viết'}
          aria-haspopup="dialog"
          aria-expanded={showSavePopover || showSavePopoverV2}
        >
          <Bookmark
            size={15}
            fill={safeIsSaved ? NEON_AMBER : 'none'}
            className="transition-transform group-active:scale-125"
          />
          {safeSavesCount > 0 && <span className="tabular-nums">{safeSavesCount}</span>}
        </motion.button>
      </div>

      {/* Save popovers (V1 + V2) — still anchored to the
          bookmark button ref forwarded from the parent. They
          must live OUTSIDE the rounded container so they
          don't get clipped by the overflow-hidden pill. */}
      <SocialSavePopover
        postId={post.id}
        currentFolder={post.savedFolder ?? null}
        isSaved={safeIsSaved}
        collections={cachedCollections}
        onCommit={handleSaveCommit}
        anchorRef={saveButtonRef}
        open={showSavePopover}
        onClose={() => setShowSavePopover(false)}
      />
      <SocialSavePopoverV2
        postId={post.id}
        collections={collectionsV2}
        context={saveContext}
        loading={collectionsV2Loading}
        onCommit={handleCommitV2}
        onCreateCollection={handleCreateV2}
        anchorRef={saveButtonRef}
        open={showSavePopoverV2}
        onClose={() => setShowSavePopoverV2(false)}
      />
      {/* ─── Phase 6 — Send-to-Messenger modal (Instagram-style) ──── */}
      <SendToMessengerModal
        open={showMessengerPicker}
        onClose={() => setShowMessengerPicker(false)}
        post={post}
        authorDisplay={
          (post as any)?.author?.displayName
          || (post as any)?.author?.fullName
          || (post as any)?.author?.username
          || 'Bài viết'
        }
      />
    </>
  );
}

// ─── SendToMessengerModal ───────────────────────────────────────────────
// Phase 6 — Instagram-style "Send post to a chat thread" modal.
// Opens when the user taps the Send icon in the action bar. Shows
// the recent threads list; tapping a row sends a message with the
// post URL + a short preview. The action bar triggers this via
// the `showMessengerPicker` / `setShowMessengerPicker` props.
function SendToMessengerModal({
  open,
  onClose,
  post,
  authorDisplay,
}: {
  open: boolean;
  onClose: () => void;
  post: SocialPost;
  authorDisplay: string;
}) {
  // Lazily fetch the thread list when the modal opens so we don't
  // burn an API call on every feed card.
  const threads = useMessagingStore((s) => s.threads);
  const threadsLoading = useMessagingStore((s) => s.threadsLoading);
  const threadsLoaded = useMessagingStore((s) => s.threadsLoaded);
  const loadThreads = useMessagingStore((s) => s.loadThreads);
  const sendMessage = useMessagingStore((s) => s.sendMessage);

  useEffect(() => {
    if (open && !threadsLoaded && !threadsLoading) {
      loadThreads().catch(() => { /* toast handled in store */ });
    }
    // We intentionally depend only on `open` so a re-fetch happens
    // only when the modal opens, not on every thread update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [sendingTo, setSendingTo] = useState<number | null>(null);

  const postUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/?post=${post.id}`
    : `/?post=${post.id}`;
  const previewText = `📌 Bài viết của ${authorDisplay}${post.content ? `: ${post.content.slice(0, 120)}` : ''}\n${postUrl}`;

  const handleSend = async (thread: MessagingThread) => {
    if (sendingTo) return;
    setSendingTo(thread.id);
    try {
      // Phase 6: Send with postShare for clickable post preview card
      await sendMessage(thread.id, previewText, undefined, undefined, { postId: post.id });
      toast.success(`Đã gửi tới ${thread.peer?.displayName || thread.peer?.username || 'cuộc trò chuyện'}`);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Gửi thất bại');
    } finally {
      setSendingTo(null);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label="Gửi qua Messenger"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="relative flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-theme bg-theme shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-theme px-5 py-3.5">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                <Send size={16} className="text-neon-violet" />
                Gửi qua Messenger
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1.5 text-text-muted transition-colors bg-theme-hover hover:text-text-primary"
                aria-label="Đóng"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Post preview chip — Instagram shows the post as a
                visual chip above the thread list. We render the
                author + a one-line content snippet so the user
                knows what they're about to send. */}
            <div className="border-b border-theme bg-theme-surface px-5 py-3">
              <div className="text-xs text-text-muted">Bài viết sẽ được gửi:</div>
              <div className="mt-1 truncate text-sm font-medium text-text-primary">
                {authorDisplay}
                {post.content && (
                  <span className="text-text-muted"> — {post.content.slice(0, 80)}{post.content.length > 80 ? '…' : ''}</span>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {threadsLoading && threads.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
                </div>
              ) : threads.length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-text-muted">
                  Bạn chưa có cuộc trò chuyện nào.
                  <br />
                  Hãy bắt đầu một cuộc trò chuyện trong trang Messenger trước.
                </div>
              ) : (
                <ul className="divide-y divide-darkborder/40">
                  {threads.map((t) => {
                    const peer = t.peer;
                    const lastMsg = t.lastMessage;
                    const isSending = sendingTo === t.id;
                    return (
                      <li key={t.id}>
                        <button
                          type="button"
                          onClick={() => handleSend(t)}
                          disabled={!!sendingTo}
                          className="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors bg-theme-hover disabled:opacity-60"
                        >
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-neon-violet/40 to-neon-pink/40 text-sm font-bold text-white">
                            {peer?.avatarUrl ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img src={peer.avatarUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <span>{(peer?.displayName || peer?.username || '?').charAt(0).toUpperCase()}</span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold text-text-primary">
                              {peer?.displayName || peer?.username || 'Cuộc trò chuyện'}
                            </div>
                            <div className="truncate text-xs text-text-muted">
                              {lastMsg?.content?.slice(0, 60) || (t.type === 'ADMIN' ? 'Hỗ trợ' : 'Bắt đầu trò chuyện')}
                            </div>
                          </div>
                          {isSending ? (
                            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-neon-violet" />
                          ) : (
                            <Send className="h-4 w-4 shrink-0 text-text-muted" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Phase 5 home upgrade: shimmer skeleton (instead of the old
// flat animate-pulse). The skeleton uses a moving linear-gradient
// overlay so it reads as "real content loading" not "broken UI".
// The gradient moves via a single CSS animation on the wrapper
// — pure GPU work, no per-frame React renders.
function CommentSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-2.5">
          <div className="shimmer h-8 w-8 flex-shrink-0 rounded-full" />
          <div className="flex-1 space-y-1.5 pt-1">
            <div className="shimmer h-3 w-24 rounded" />
            <div className="shimmer h-3 w-4/5 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ─── YouTube Embed ──────────────────────────────────────────────────────

function YouTubeEmbed({ url }: { url: string }) {
  // Same regex the composer uses to extract the video id from any
  // of the common YouTube URL shapes (watch, youtu.be, /shorts,
  // /embed, or a bare 11-char id).
  const id = (() => {
    if (!url) return null;
    if (/^[A-Za-z0-9_-]{11}$/.test(url)) return url;
    let m = url.match(/youtube\.com\/watch\?(?:.*&)?v=([A-Za-z0-9_-]{11})/);
    if (m) return m[1];
    m = url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
    if (m) return m[1];
    m = url.match(/youtube\.com\/(?:shorts|embed)\/([A-Za-z0-9_-]{11})/);
    if (m) return m[1];
    return null;
  })();

  if (!id) {
    // Fallback: render a clickable link card when we can't make
    // sense of the URL.
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 flex items-center gap-3 rounded-2xl p-3 transition-colors"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-light)' }}
      >
        <Youtube className="h-6 w-6 text-red-500 shrink-0" />
        <span className="truncate text-sm" style={{ color: 'var(--text-secondary)' }}>{url}</span>
      </a>
    );
  }

  return (
    <div
      className="mt-3 overflow-hidden rounded-2xl"
      style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid var(--border-light)' }}
    >
      <div className="relative aspect-video">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}`}
          title="YouTube video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 h-full w-full"
          loading="lazy"
        />
      </div>
    </div>
  );
}

// ─── Shimmering skeleton loader ─────────────────────────────────
// Replaces the previous flat `animate-pulse` block with a moving
// gradient that slides from left to right. Two variants share the
// same shimmer keyframe so the page looks visually consistent while
// the feed is hydrating.

function ShimmerBlock({
  className,
  rounded = 'rounded',
}: {
  className: string;
  rounded?: string;
}) {
  return (
    <div
      className={`relative overflow-hidden ${rounded} ${className}`}
      style={{ background: 'var(--bg-surface-active)' }}
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, rgba(139,92,246,0.10) 50%, transparent 100%)',
          animation: 'social-shimmer 1.6s ease-in-out infinite',
        }}
      />
    </div>
  );
}

export function PostSkeleton() {
  return (
    <div
      className="rounded-2xl p-5"
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-light)',
        backdropFilter: 'blur(20px)',
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <ShimmerBlock className="w-10 h-10" rounded="rounded-full" />
        <div className="flex-1 space-y-2">
          <ShimmerBlock className="h-3 w-24" />
          <ShimmerBlock className="h-2.5 w-16" />
        </div>
        <ShimmerBlock className="w-6 h-6" rounded="rounded" />
      </div>
      {/* Content lines */}
      <div className="space-y-2 mb-4">
        <ShimmerBlock className="h-3 w-full" />
        <ShimmerBlock className="h-3 w-5/6" />
        <ShimmerBlock className="h-3 w-4/6" />
      </div>
      {/* Action bar */}
      <div
        className="flex items-center gap-6 pt-3"
        style={{ borderTop: '1px solid var(--border-light)' }}
      >
        <ShimmerBlock className="h-7 w-16" rounded="rounded-lg" />
        <ShimmerBlock className="h-7 w-16" rounded="rounded-lg" />
        <ShimmerBlock className="h-7 w-16" rounded="rounded-lg" />
      </div>
    </div>
  );
}
